/**
 * Wizard Context - Shared state management for procurement wizard
 * Used by both NewProcurement and ContinueProcurement pages
 */
import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';
import api from '@/lib/api';
import { toastSuccess, toastError, toastWarning, toastInfo } from '@/lib/toast';
// normalizeQuestion/isAnswerEmpty live in lib/businessQuestions.js (answers-
// step batch, 2026-07-22) to avoid a circular import with
// lib/answersValidation.js (which also needs normalizeQuestion) - imported
// here for this file's own internal use and re-exported below unchanged.
import { normalizeQuestion, isAnswerEmpty } from '@/lib/businessQuestions';
// Item 7 (answers-step batch, 2026-07-22): the same temporal-validity
// check StepAnswers uses at submit-time, reused here for resume-time
// revalidation (see loadProcurement/temporalBlock below) - one shared
// implementation, not two that can drift. Non-circular: answersValidation.js
// only depends on lib/businessQuestions.js, never on this file.
// Item 8 (date-validation-fixes batch, 2026-07-23): once a procurement has
// reached Lines, LINE dates are authoritative (same source the
// generate_rfp gate now checks) - the resume-time check must mirror that,
// not the separately-answered need-by/start date, so resume and the gate
// can't drift.
// appendRuleCodeSuffix only: the resume-time date check that used the
// rule evaluators was removed from this file on 2026-08-31 (see
// loadProcurement). The suffix is still needed for the generate-rfp
// TEMPORAL_BLOCK toast, which is a transition being refused.
import { appendRuleCodeSuffix } from '@/lib/dateValidationRules';
import { applyResolvedLocationToLines } from '@/lib/lineItemLocationBackfill';
import { RETIRED_CATEGORY_REPLACED } from '@/lib/categoryGateDecisionContext';
// F5 (2026-08-29): a revision entry may declare its question REMOVED. Every
// place this file turns revised_vendor_questions into something the buyer
// sees or submits must split the array through these, never read it whole.
import { keptEntries, removedEntries } from '@/lib/vendorQuestionRemoval';
import {
  ADVISORY_STALE,
  HEADER_CATEGORY_FIELD,
  RISK_CATEGORY_ATTESTATION_REQUIRED,
  describeStaleReferencesForToast,
  referencesIncludeHeader,
  regenerateStepFor,
} from '@/lib/masterReferenceStaleness';
// Wizard steps configuration.
//
// INTAKE COMPRESSION V1 (Ashok ruling, 2026-08-20): 'uploads' is GONE - the
// two upload boxes now live on Intake, so the requirement, the BOQ and the
// attached documents are collected on one screen and the parse runs ONCE
// with all of them (memory/DESIGN-intake-state-model.md §1). Every index
// above the old 1 therefore decrements by one.
//
// THE POSITIONAL RENDER ARRAYS IN NewProcurement.jsx AND
// ContinueProcurement.jsx MUST MATCH THIS LIST, element for element. They
// are hand-maintained duplicates that nothing used to check; they are now
// pinned by frontend/src/components/wizard/stepModel.parity.test.jsx.
export const WIZARD_STEPS = [
  { id: 'intake', label: 'Intake' },
  { id: 'questions', label: 'Questions' },
  { id: 'answers', label: 'Answers' },
  { id: 'review', label: 'Review' },
  { id: 'items', label: 'Line Items' },
  { id: 'rfp', label: 'RFP' },
  { id: 'risk', label: 'Risk' }
];

// Frontend mirror of the dependency map (spec: memory/DESIGN-wizard-
// invalidation.md §3). Editing a step must mark exactly its dependents
// stale - never a blanket "everything after step N".
//
// The 'uploads' key is removed with the step. Its dependent list was
// identical to 'intake' - which is what made the merge safe here: the two
// steps already invalidated exactly the same things, so folding them
// changes no invalidation behaviour.
export const STEP_DEPENDENTS = {
  intake: ['questions', 'answers', 'review', 'items', 'rfp', 'risk'],
  questions: ['answers', 'review', 'items', 'rfp', 'risk'],
  answers: ['review', 'items', 'rfp', 'risk'],
  review: ['items', 'rfp', 'risk'],
  items: ['rfp', 'risk'],
  rfp: [],
  risk: [],
};

// Map backend status to THE NEXT STEP TO DO.
//
// DELIBERATELY DIFFERENT FROM ProcurementDetail.jsx's same-named function,
// which maps status to the LAST STEP DONE for its read-only progress
// banner. The two are an unregistered same-language mirror pair; they are
// PINNED AGAINST LITERALS, with the divergence declared, by
// stepModel.parity.test.jsx. Do not unify them - they answer different
// questions and only one of them drives navigation.
//
// 'intake' NOW RESUMES TO 0, NOT 1 (Intake Compression V1). There is no
// Uploads step to land on, and step 0 is the merged Intake screen. The
// consequence is the crux of this slice: isStepPreviouslyCompleted('intake')
// is `0 < furthestStepReached` and so is FALSE on arrival, for all 89 live
// documents at this status. The merged step therefore must NOT use it as
// its mode discriminator - it reads parsed_requirement presence instead.
// See hasParsedRequirement below and StepIntake.jsx.
export const getStepFromStatus = (status) => {
  switch (status) {
    case 'intake': return 0;
    case 'questions_generated': return 2;
    // A blocking consistency error at submit-answers (G-1): resume must land
    // back on Answers, editable, with the blocking issue visible - not fall
    // through to the default (Intake), which gave no clue what to fix.
    case 'answers_need_correction': return 2;
    case 'answers_provided': return 3;
    case 'refined': return 4;
    case 'rfp_generated': return 6;
    case 'completed': return 6;
    default: return 0;
  }
};

// THE MODE DISCRIMINATOR (Ashok ruling, 2026-08-20).
//
// A draft and a parsed procurement carry the SAME status ("intake") - that
// is what the no-new-status-value ruling requires - so status cannot tell
// them apart, and after the merge neither can step index: both are 0.
// The only honest discriminator is the data itself.
//
//   parsed_requirement ABSENT  -> a draft; the merged Intake step is editable
//   parsed_requirement PRESENT -> parsed; the merged Intake step is read-only
//
// NOT isStepPreviouslyCompleted('intake'), which is the trap this function
// exists to avoid: it evaluates `0 < furthestStepReached`, false on arrival
// at step 0, so every resumed document would have rendered its requirement
// box empty and editable.
//
// The backend guarantees ABSENCE rather than null for a draft
// (routes/procurement.py's _draft_document_fields pops the key), so a plain
// presence test is sound; the extra emptiness check below costs nothing and
// covers a legacy document that stored `{}`.
export const hasParsedRequirement = (parsedRequirement) => {
  if (!parsedRequirement || typeof parsedRequirement !== 'object') return false;
  return Object.keys(parsedRequirement).length > 0;
};

// Detect procurement type from category
export const detectProcurementType = (category) => {
  if (!category) return 'mixed';
  const upperCat = category.toUpperCase();
  
  if (['IT-HW', 'IT-SW', 'EQ', 'MRO', 'RAW', 'FURN', 'OFF'].some(g => upperCat.includes(g))) {
    return 'goods';
  }
  if (['PS', 'CONS', 'SVC', 'MAINT', 'SUPPORT', 'SERVICE', 'PROFESSIONAL'].some(s => upperCat.includes(s))) {
    return 'services';
  }
  return 'mixed';
};

// PROC-000054 fix: the ONE procurement_type derivation, used both by
// getProcurementType (live wizard state) and loadProcurement's resume-time
// temporal check - previously each had its own copy of the fallback chain
// below the first line, which is how the two checks could drift. Priority:
// (1) docProcurementType - the value the backend's generation-time contract
// check actually verified the current business_questions set against (see
// routes/procurement.py generate-questions); once present, this is
// authoritative and nothing below it is consulted. (2) refined_requirement's
// buyer_input, set by the refinement agent. (3) a guess from the parsed
// category. (4) 'mixed', unchanged legacy default for a procurement with
// none of the above (pre-fix and never regenerated since).
export const resolveProcurementType = ({ docProcurementType, refinedRequirement, parsedRequirement }) => {
  if (docProcurementType) return docProcurementType;
  if (refinedRequirement?.buyer_input?.procurement_type) return refinedRequirement.buyer_input.procurement_type;
  if (parsedRequirement?.category) return detectProcurementType(parsedRequirement.category);
  return 'mixed';
};

export { normalizeQuestion, isAnswerEmpty };

// Answers staleness semantics (spec §4): Answers is user-authored, so a
// question-set change never "regenerates" it the way Items/RFP/Risk
// regenerate - it reconciles the existing answers map against the new
// question list instead. Matches primarily by field_key (Issue 9: a
// reworded AI question keeps the same field_key, so text-only matching
// dropped its answer on every reword) and falls back to matching by
// CONTENT (normalized question text) for questions with no field_key -
// e.g. user-added freeform questions. Neither matches by array position,
// since questions have no stable id and can be inserted/removed/reordered -
// preserves the answer for a matched question, defaults a genuinely new
// question's answer to empty, and silently drops the answer for a question
// that no longer exists.
export const reconcileAnswersForQuestions = (oldQuestions, newQuestions, oldAnswers) => {
  const oldKeyByFieldKey = {};
  const oldKeyByText = {};
  (oldQuestions || []).forEach((q, i) => {
    const nq = normalizeQuestion(q);
    const text = nq.question.trim();
    if (nq.field_key && !(nq.field_key in oldKeyByFieldKey)) oldKeyByFieldKey[nq.field_key] = `q${i}`;
    if (text && !(text in oldKeyByText)) oldKeyByText[text] = `q${i}`;
  });
  const newAnswers = {};
  // Preserve any answer keyed OUTSIDE the positional q{i} scheme - e.g. the
  // Answers step's own "Any other suggestion" free-text field (Issue 19,
  // 2026-07-21), which isn't tied to a specific business question's
  // position and must survive a Questions-step reconciliation untouched.
  // No existing code path writes a non-q{i} key today, so this is a no-op
  // for every prior answers shape.
  Object.keys(oldAnswers || {}).forEach((key) => {
    if (!/^q\d+$/.test(key)) newAnswers[key] = oldAnswers[key];
  });
  (newQuestions || []).forEach((q, j) => {
    const nq = normalizeQuestion(q);
    const text = nq.question.trim();
    let oldKey;
    if (nq.field_key && nq.field_key in oldKeyByFieldKey) {
      oldKey = oldKeyByFieldKey[nq.field_key];
    } else if (text && text in oldKeyByText) {
      oldKey = oldKeyByText[text];
    }
    newAnswers[`q${j}`] = oldKey ? (oldAnswers?.[oldKey] ?? '') : '';
  });
  return newAnswers;
};

/**
 * Bug fix (2026-07-27): a user's manual edit to a business question's TEXT
 * invalidates its AI-generated answer suggestions - they were generated
 * for the OLD wording and are stale the instant the wording changes.
 * Repro: "Which SAP modules are relevant?" edited to "How much experience
 * do you have in SAP HANA?" kept rendering the FIRST question's
 * suggestion chips under the second - handleEditBusinessQuestion used to
 * spread `{ ...current, question: value }`, which preserves EVERY other
 * field including `suggestions` unchanged. field_key/answer_type/
 * is_mandatory/default_value are the question's STABLE identity/contract
 * (independent of exact wording) and correctly survive a text edit
 * unchanged - only `suggestions` (content-derived, paired with the exact
 * wording they were generated for) must be cleared, never regenerated via
 * an LLM call just for a manual text edit (see QUESTION-GENERATOR
 * DOCUMENT BUDGET/PROMPT FENCING batch's "Tier-1 only" spirit - this is a
 * local reducer fix, not a new generation path). A legacy plain-string
 * question has no other fields to preserve, so it's simply replaced by
 * the new text, same as before.
 */
export const applyBusinessQuestionTextEdit = (current, newText) =>
  (current && typeof current === 'object')
    ? { ...current, question: newText, suggestions: [] }
    : newText;

const CURRENCY_FIELD_KEYS = ['rfp_currency', 'currency', 'quotation_currency', 'quote_currency'];
const KNOWN_CURRENCY_CODES = ['INR', 'USD', 'GBP', 'AED', 'AUD', 'EUR', 'SGD'];

export const extractCurrencyAnswer = (questions, answersMap) => {
  const idx = questions.findIndex(q => CURRENCY_FIELD_KEYS.includes(normalizeQuestion(q).field_key));
  if (idx !== -1) {
    const val = answersMap[`q${idx}`];
    if (!isAnswerEmpty(val)) return val;
  }
  for (const val of Object.values(answersMap)) {
    if (typeof val === 'string' && KNOWN_CURRENCY_CODES.includes(val.trim().toUpperCase())) {
      return val.trim().toUpperCase();
    }
  }
  return null;
};

export const extractLocationAnswer = (questions, answersMap) => {
  // service_location is free text (city/venue/hotel) and must never be matched
  // against the location master dropdown - only ship_to_location (and its
  // delivery-address aliases) pre-fills the internal location_id.
  const GOODS_LOCATION_KEYS = ['ship_to_location', 'delivery_location', 'delivery_address'];
  const SERVICE_LOCATION_KEYS = ['service_location'];

  const idx = questions.findIndex(q => {
    const key = normalizeQuestion(q).field_key;
    return GOODS_LOCATION_KEYS.includes(key) && !SERVICE_LOCATION_KEYS.includes(key);
  });
  if (idx !== -1) {
    const val = answersMap[`q${idx}`];
    if (!isAnswerEmpty(val)) {
      return (val && typeof val === 'object' && val.location_id) ? val.location_id : val;
    }
  }
  for (const val of Object.values(answersMap)) {
    if (val && typeof val === 'object' && val.location_id) {
      return val.location_id;
    }
  }
  return null;
};

// Failure contract (item 1, G-2/G-4): the backend's shared agent-result
// validation (routes/_contracts.py's call_agent_with_contract) responds 502
// with detail={agent, message, raw_response} when an agent fails parsing or
// returns empty/missing required output, after one automatic server-side
// retry. Nothing is ever persisted on that path, so a client-side retry is
// always safe - this surfaces the failure with a one-click Retry action
// that just re-invokes the same handler, reusing the existing toast pattern
// rather than introducing a new error-UI framework.
// A TRUNCATION IS DETERMINISTIC (2026-09-04, Ashok's 50-row BOQ). When the
// agent's response hit its output ceiling, the 502 detail carries
// `truncated: true` (routes/_contracts.py). The same input produces the same
// overflow, the server already ran it twice, and each Retry click would
// spend two more full-ceiling attempts (~$0.24 of output per Continue) for
// the same result. So no Retry, and the message says what actually helps.
// EVERYTHING ELSE KEEPS RETRY: a 500, a 503, a network error with no
// response at all, and a 502 whose detail is a parse failure or a missing
// key are all things a second attempt can fix. Only the boolean decides -
// never a match on the message text, which is the server's to change.
export const TRUNCATION_MESSAGE =
  'The analysis ran out of room writing the line items for this requirement. '
  + 'Retrying will not help - the same input produces the same result. '
  + 'Upload a BOQ with fewer rows, then continue again.';

// A STALE MASTER REFERENCE IS DETERMINISTIC TOO (staleness gate, 2026-09-15).
// Every forward boundary - generate-questions, submit-answers,
// finalize-questions, generate-rfp, assess-risk - refuses with a 422 whose
// detail is {code: 'MASTER_REFERENCE_STALE', message, stale_references} when
// a category stored on the document is no longer active in the master
// (routes/procurement.require_current_master_references). Nothing about
// re-running changes that - only the buyer reselecting, or the master being
// reactivated, clears it - so the sentence is shown and Retry is withheld.
// Branched on the CODE, never on the prose, like CATEGORY_NO_FIT_BLOCK.
// The literal is pinned from the backend side by
// test_master_reference_guard_wiring.py.
export const MASTER_REFERENCE_STALE = 'MASTER_REFERENCE_STALE';

export const isMasterReferenceStale = (error) => {
  const detail = error?.response?.data?.detail;
  return error?.response?.status === 422
    && Boolean(detail) && typeof detail === 'object' && !Array.isArray(detail)
    && detail.code === MASTER_REFERENCE_STALE;
};

// `options.totalLines` - the document's final line count when the caller
// knows it - lets a staleness refusal say "50 of 51 lines" (see below).
export const showGenerationError = (error, fallbackMessage, retryFn, options = {}) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const truncated = status === 502 && detail && typeof detail === 'object' && detail.truncated === true;
  const stale = isMasterReferenceStale(error);
  let message = fallbackMessage;

  if (stale) {
    // GROUPED BY CATEGORY CODE (Ashok ruling, 2026-09-15). The server's
    // `message` names every reference ("line 1 (601090); line 2 (601090);
    // ..." - 1,139 characters for PROC-000412); the toast says each distinct
    // code once, where it is used and how many lines, with the line numbers
    // compressed into ranges. The 422 itself is untouched: `stale_references`
    // is still the full list, and `message` is the fallback if it is absent.
    message = (Array.isArray(detail.stale_references) && detail.stale_references.length)
      ? describeStaleReferencesForToast(detail.stale_references, options.totalLines ?? null)
      : (detail.message || fallbackMessage);
  } else if (truncated) {
    message = TRUNCATION_MESSAGE;
    console.error('Agent failure detail:', detail);
  } else if (status === 502 && detail && typeof detail === 'object') {
    message = `Generation failed (${detail.agent || 'agent'}) - please retry. ${detail.message || ''}`.trim();
    console.error('Agent failure detail:', detail);
  } else if (typeof detail === 'string') {
    message = detail;
  }

  // Freeze boundary fix (2026-07-26): a 409 is a permanent state conflict
  // (e.g. update-intake's "This procurement is completed and can no longer
  // be edited.") - offering a "Retry" action would just hit the same 409
  // again forever, unlike a 502's often-transient agent failure. A
  // truncation is the other permanent case (above).
  const offerRetry = retryFn && status !== 409 && !truncated && !stale;
  toastError(message, offerRetry ? {
    duration: 8000,
    action: { label: 'Retry', onClick: retryFn }
  } : undefined);
};

const WizardContext = createContext(null);

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};

export const WizardProvider = ({ children, initialProcurementId = null, onUsageChanged }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  // Measured height of the shared WizardHeader strip (title + step
  // indicator), so a step that needs its own secondary sticky row (e.g.
  // StepRisk's score header) can pin flush beneath it with zero pre-pin
  // travel. Set by WizardHeader via a ResizeObserver.
  const [wizardHeaderHeight, setWizardHeaderHeight] = useState(0);

  // Core data state
  const [procurementId, setProcurementId] = useState(initialProcurementId);
  const [serialId, setSerialId] = useState(null);  // Unique serial ID like PROC-000001
  const [userRequirement, setUserRequirement] = useState('');
  const [parsedRequirement, setParsedRequirement] = useState(null);
  // C1b-1c: does the header category resolve to an ACTIVE category_master
  // row? true / false / null-for-unknown, supplied by the backend on every
  // route that sets parsedRequirement above. NEVER derived here - a JS copy
  // of the shape check plus an active-membership test would be a new
  // mirror pair in CLAUDE.md's registry, hand-maintained and divergent the
  // first time either side is edited. null means the category master could
  // not be read, which is deliberately NOT the same as false: see
  // routes/procurement.category_active_resolvable.
  const [categoryActiveResolvable, setCategoryActiveResolvable] = useState(null);
  // THE RESUME ADVISORY (staleness gate, 2026-09-15). The server's
  // {status, stale_references}, from GET's master_reference_advisory on
  // resume or from the `advisory` a boundary refusal carries. INFORMS ONLY -
  // nothing in the wizard blocks on it; the next boundary refuses. Never
  // computed here (see lib/masterReferenceStaleness.js).
  const [masterReferenceAdvisory, setMasterReferenceAdvisory] = useState(null);
  // The stored status, for the advisory's step links only (2026-09-17): a
  // completed procurement is a record, so its step names render as text.
  // Nothing else branches on it; resume still reads data.status directly.
  const [procurementStatus, setProcurementStatus] = useState(null);
  // The STORED final line count - the denominator of "50 of 51 lines" on the
  // advisory and the refusal toast. The guard reads final_line_items only, so
  // this is set from GET's final_line_items on resume and from the lines just
  // saved by finalize-line-items; null until known (the count is then given
  // bare, never guessed). Not lineItems.length: on resume that falls back to
  // the AI baseline when nothing was finalized.
  const [finalLineCount, setFinalLineCount] = useState(null);
  // THE RISK-STAGE FORK: the refusal's detail while the dialog is open, else
  // null. riskForkBusy while an answer is being recorded.
  const [riskFork, setRiskFork] = useState(null);
  const [riskForkBusy, setRiskForkBusy] = useState(false);
  // ECHO_METADATA (Ashok ruling, 2026-07-26): structured stated-intent
  // capture emitted alongside (never nested inside) parsed_requirement -
  // memory/DESIGN-services-engagement-models.md. SOFT - null on legacy
  // docs or a malformed agent response; SARV/SARD (StepAnswers.jsx) treat
  // that as a silent bypass, never an error.
  const [echoMetadata, setEchoMetadata] = useState(null);
  // PROC-000054 fix: the procurement_type the BACKEND actually verified the
  // current business_questions set against (persisted by generate-questions'
  // contract check - see routes/procurement.py's _missing_mandatory_field_keys_check).
  // getProcurementType() below reads this FIRST, ahead of any client-side
  // re-derivation, so a question set that just passed generation can never
  // be re-flagged as incomplete by the read-time check using a different
  // guess at the type. null means this procurement has never had a
  // contract-verified generation (or predates this fix) - falls through to
  // the legacy derivation.
  const [docProcurementType, setDocProcurementType] = useState(null);
  const [businessQuestions, setBusinessQuestions] = useState([]);
  const [vendorQuestions, setVendorQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [revisedVendorQuestions, setRevisedVendorQuestions] = useState([]);
  // F5 (2026-08-29): entries the agent declared removed under an
  // applicability verdict. Held separately from revisedVendorQuestions - they
  // are never editable, never submitted, and shown at Review with their
  // reason so the removal leaves a trace the buyer can see (ruling 3).
  const [removedVendorQuestions, setRemovedVendorQuestions] = useState([]);
  const [revisionSummary, setRevisionSummary] = useState('');
  const [editableQuestions, setEditableQuestions] = useState([]);
  const [refinedRequirement, setRefinedRequirement] = useState(null);
  // THE RESOLVED CATEGORY, SUPPLIED BY THE BACKEND (C1b-1a, 2026-08-14).
  // Populated from canonical_category, a computed (never stored) field on
  // the two responses that can put this wizard on the Lines step: GET
  // /{id} (resume) and finalize-questions (fresh session). The resolution
  // rule itself lives in ONE place, routes/procurement.py's
  // canonical_category_code - re-deriving it here from parsed_requirement
  // would put a second copy of that rule in a second language.
  const [canonicalCategory, setCanonicalCategory] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [rfpDocument, setRfpDocument] = useState(null);
  const [rfpVersion, setRfpVersion] = useState(null);
  const [riskAssessment, setRiskAssessment] = useState(null);
  // Version-aware feedback (RFP can regenerate to a new version under P2) -
  // null means never submitted; otherwise the rfp_version feedback was
  // given for. "Currently received" = this equals rfpVersion.
  const [feedbackSubmittedVersion, setFeedbackSubmittedVersion] = useState(null);
  const [boqItems, setBoqItems] = useState([]);
  const [boqSource, setBoqSource] = useState(null);
  const [attachedDocuments, setAttachedDocuments] = useState([]);
  const [consistencyValidation, setConsistencyValidation] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);  // RFP currency selection
  const [answeredShipToLocation, setAnsweredShipToLocation] = useState(null);  // Raw location answer, captured at Answers step
  // Item 7 (answers-step batch, 2026-07-22): {field, message} | null - set
  // by loadProcurement's resume-time check (trigger a) or
  // handleFinalizeLineItems catching the backend's TEMPORAL_BLOCK (trigger
  // b, the RFP-generation gate). StepAnswers renders this as a one-time
  // explaining dialog + a persistent blocking banner; cleared the same way
  // any other hard-validation failure clears, on the next successful
  // handleAnswersSubmit.
  const [temporalBlock, setTemporalBlock] = useState(null);
  const [requirementLineItems, setRequirementLineItems] = useState([]);  // AI-generated baseline (backend's requirement_line_items) - separate from the user-editable `lineItems`, kept for "Restore AI-generated lines"
  // Hard line-item validation rejection from finalize-line-items
  // (BACKLOG.md Batch 1, blocker 2026-07-21) - [{line_index, field,
  // message}] | null. This is the backend's authoritative backstop for a
  // stale client whose own inline checks somehow missed something (e.g. a
  // spend_category that existed when the client's category list loaded but
  // was since removed from master data); LineItemsEditor merges it
  // alongside its own live client-side computation so both render
  // identically. Cleared as soon as lineItems changes (any edit deserves a
  // fresh check) - see the effect below.
  const [lineItemErrors, setLineItemErrors] = useState(null);

  // --- Wizard invalidation state (spec P2 §4) ---
  const [staleStepIds, setStaleStepIds] = useState(() => new Set());
  const [stepEditUnlocked, setStepEditUnlocked] = useState(() => new Set());
  const [furthestStepReached, setFurthestStepReached] = useState(0);
  const [viewedSteps, setViewedSteps] = useState(() => new Set());
  const [regeneratedNotice, setRegeneratedNotice] = useState(null);  // { stepId, causeLabel } | null - one-line "Regenerated because X changed" banner
  const entrySnapshotsRef = useRef({});  // stepId -> deep-cloned state captured at unlock time, for the "did anything actually change" check on Continue
  // Suppresses exactly one firing of the "clear consistencyValidation on
  // answers change" effect below - set synchronously right before
  // loadProcurement's bulk setAnswers(data.user_answers) so that rehydrating
  // a stored consistencyValidation isn't immediately wiped by that effect,
  // which would otherwise treat the load's one-time answers replacement the
  // same as a genuine user edit. Consumed (reset false) the first time the
  // effect runs, so real subsequent edits still clear normally.
  const suppressConsistencyClearRef = useRef(false);

  // =====================================================================
  // NOTHING REACHES THE SERVER UNTIL CONTINUE (Ashok ruling, 2026-08-21)
  // =====================================================================
  // The merged Intake screen collects a requirement and files together. It
  // used to need a saved draft first, because upload-boq and
  // upload-document take a procurement_id in the path - so "attach a file"
  // became a visible save-first step, and abandoning the screen left a
  // document and a burnt serial behind. Both are now held HERE, in the
  // browser, and posted in one orchestrated sequence at Continue.
  //
  // THE TEMP REQUEST ID IS A useRef, NOT STATE, AND THAT IS DELIBERATE.
  //   * It must survive a step re-render AND a re-mount of StepIntake.
  //     StepIntake unmounts whenever currentStep moves, so component state
  //     would lose it; the provider sits above the step switch in both
  //     NewProcurement.jsx and ContinueProcurement.jsx and does not
  //     unmount, so anything held here outlives every step change.
  //   * It is never rendered, so it has no business triggering one. Making
  //     it state would put it in the render path and invite it onto the
  //     screen - and the buyer must see NO identifier until the real
  //     serial exists. A ref cannot be displayed by accident.
  //   * It is NEVER SENT. The server never learns it. It exists only to
  //     group this drafting session's text and files on the client, and to
  //     give a held document a stable local key for its list row.
  const tempRequestIdRef = useRef(null);
  const getTempRequestId = useCallback(() => {
    if (!tempRequestIdRef.current) {
      // crypto.randomUUID where available; the fallback keeps jsdom and
      // any non-secure-context browser working. Uniqueness only has to
      // hold within one browser session - this value never leaves it.
      tempRequestIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? `tmp-${crypto.randomUUID()}`
        : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    return tempRequestIdRef.current;
  }, []);

  // The actual File objects, held until Continue uploads them.
  // heldBoqFile is the file; its PARSED ROWS live in boqItems, populated by
  // the stateless preview endpoint, so the existing preview table renders
  // unchanged and downstream line-item prefill keeps reading one source.
  const [heldBoqFile, setHeldBoqFile] = useState(null);
  // [{ id, file, filename, document_type, size }] - id is client-side only.
  // Kept SEPARATE from attachedDocuments rather than mixed into it:
  // attachedDocuments is server truth, it is JSON.stringify'd by the
  // intake-edit snapshot comparison, and a File stringifies to {} - so two
  // different held files would compare equal and an edit would read as no
  // change.
  const [heldDocuments, setHeldDocuments] = useState([]);

  const clearHeldFiles = useCallback(() => {
    setHeldBoqFile(null);
    setHeldDocuments([]);
    tempRequestIdRef.current = null;
  }, []);

  // --- Unsaved-changes navigation guard (pre-merge item) ---
  // ONE shared dirty-state, not bespoke per-step guards: a step marks
  // itself dirty on a genuine local edit (via markStepDirty, called from
  // the step component/handler at the point of the edit - never from a
  // programmatic load/rehydration/regeneration path) and its own
  // successful forward-persist clears it (via clearStepDirty). See the
  // useBlocker/beforeunload effects below for the single guard that reads
  // this and intercepts every exit vector.
  //
  // Steps covered vs excluded, and why:
  //   Covered: intake, questions (business questions), answers, review
  //   (vendor questions), items (line items + currency).
  //   Excluded: risk (no editable local state); rfp (the live RFP step,
  //   StepRfpPreview -> RfpPreview, is read-only - RfpEditor.jsx's own
  //   hasUnsavedChanges is unreferenced dead code, never rendered).
  //
  //   UPLOADS WAS EXCLUDED HERE, ON THE GROUNDS THAT "uploads/deletes
  //   persist immediately - no local state to lose". THAT REASON IS NO
  //   LONGER TRUE and the exclusion is withdrawn (Intake Compression V1,
  //   2026-08-21). A selected file is now held in the browser and reaches
  //   the server only at Continue, so it is exactly the local state the
  //   guard exists to protect. A buyer who picks a BOQ and then navigates
  //   away loses it, and must be warned - INCLUDING when they have typed
  //   no requirement text at all, which is why held files are OR'd into
  //   hasUnsavedChanges below rather than folded into markStepDirty
  //   ('intake'): the text and the files are independently losable.
  const [dirtySteps, setDirtySteps] = useState(() => new Set());

  const markStepDirty = useCallback((stepId) => {
    setDirtySteps(prev => {
      if (prev.has(stepId)) return prev;
      const next = new Set(prev);
      next.add(stepId);
      return next;
    });
  }, []);

  const clearStepDirty = useCallback((stepId) => {
    setDirtySteps(prev => {
      if (!prev.has(stepId)) return prev;
      const next = new Set(prev);
      next.delete(stepId);
      return next;
    });
  }, []);

  // HELD FILES ARE DIRTY STATE IN THEIR OWN RIGHT. Not via dirtySteps,
  // because a file can be held with no step ever marked dirty - a buyer who
  // attaches a BOQ before typing a word. That case is mutant J7.
  const hasHeldFiles = Boolean(heldBoqFile) || heldDocuments.length > 0;
  const hasUnsavedChanges = dirtySteps.size > 0 || hasHeldFiles;

  // Exit vector 1: browser-level (tab close, refresh, typed URL change) -
  // native beforeunload, no router involvement needed.
  useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Exit vector 2: in-app navigation (Layout's sidebar links, the logo
  // link, any programmatic navigate() call, browser back/forward) - RRv7's
  // useBlocker, which requires the data router set up in App.js.
  const blocker = useBlocker(hasUnsavedChanges);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    const confirmed = window.confirm('You have unsaved changes on this step. Leave without saving?');
    if (confirmed) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  // A step can only ever be as far "ahead" as currentStep has reached -
  // this is what distinguishes "revisiting a completed step" (currentStep
  // < furthestStepReached) from "arriving at it for the first time"
  // (currentStep === furthestStepReached).
  useEffect(() => {
    setFurthestStepReached(prev => (currentStep > prev ? currentStep : prev));
  }, [currentStep]);

  // Per-step state: 'locked' (never reached) | 'stale' (needs regeneration)
  // | 'current' | 'completed'. Staleness takes priority over 'current' so a
  // step you've just landed on but hasn't regenerated yet still reads as
  // stale until its own handler clears it.
  const stepStates = useMemo(() => {
    const states = {};
    WIZARD_STEPS.forEach((step, i) => {
      if (i > furthestStepReached) {
        states[step.id] = 'locked';
      } else if (staleStepIds.has(step.id)) {
        states[step.id] = 'stale';
      } else if (i === currentStep) {
        states[step.id] = 'current';
      } else {
        states[step.id] = 'completed';
      }
    });
    return states;
  }, [currentStep, furthestStepReached, staleStepIds]);

  // Distinct from stepStates: that map gives i===currentStep priority (so
  // the indicator shows the step you're standing on as 'current' blue, even
  // if you got there via Back), which is right for the indicator but wrong
  // for read-only gating - navigating Back to an already-completed step
  // must still read as "completed" (read-only-by-default) even though
  // currentStep now equals its index again. This checks the step's
  // position against furthestStepReached directly, ignoring currentStep.
  const isStepPreviouslyCompleted = useCallback((stepId) => {
    const i = WIZARD_STEPS.findIndex(s => s.id === stepId);
    return i !== -1 && i < furthestStepReached && !staleStepIds.has(stepId);
  }, [furthestStepReached, staleStepIds]);

  const postWizardEvent = useCallback((stepId, event, metadata) => {
    if (!procurementId) return;
    // Best-effort audit trail - never block or fail the wizard action itself.
    api.postWizardEvent(procurementId, stepId, event, metadata).catch(() => {});
  }, [procurementId]);

  const markStepsStale = useCallback((stepIds) => {
    if (!stepIds || stepIds.length === 0) return;
    setStaleStepIds(prev => {
      const next = new Set(prev);
      stepIds.forEach(id => next.add(id));
      return next;
    });
  }, []);

  const clearStepStale = useCallback((stepId) => {
    setStaleStepIds(prev => {
      if (!prev.has(stepId)) return prev;
      const next = new Set(prev);
      next.delete(stepId);
      return next;
    });
  }, []);

  // Explicit intent gate (spec §2.3): completed steps render read-only by
  // default; this is the only way into an editable view of one.
  const unlockStepForEdit = useCallback((stepId, snapshotValue) => {
    entrySnapshotsRef.current[stepId] = snapshotValue;
    setStepEditUnlocked(prev => new Set(prev).add(stepId));
    postWizardEvent(stepId, 'edit_unlocked');
  }, [postWizardEvent]);

  const lockStepEdit = useCallback((stepId) => {
    setStepEditUnlocked(prev => {
      if (!prev.has(stepId)) return prev;
      const next = new Set(prev);
      next.delete(stepId);
      return next;
    });
  }, []);

  const isStepUnlocked = useCallback((stepId) => stepEditUnlocked.has(stepId), [stepEditUnlocked]);

  // 'viewed' event on revisiting a completed step, throttled to once per
  // step per session (viewedSteps resets on page reload/new WizardProvider).
  useEffect(() => {
    const stepId = WIZARD_STEPS[currentStep]?.id;
    if (!stepId || !procurementId) return;
    if (!isStepPreviouslyCompleted(stepId)) return;
    setViewedSteps(prev => {
      if (prev.has(stepId)) return prev;
      postWizardEvent(stepId, 'viewed');
      const next = new Set(prev);
      next.add(stepId);
      return next;
    });
  }, [currentStep, isStepPreviouslyCompleted, procurementId, postWizardEvent]);

  // Clear any stale consistency-check result as soon as the user's answers
  // change, so a corrected value doesn't leave the old error/warning banner
  // (and StepAnswers' warning dialog) showing until the next submit. Skipped
  // exactly once when the change is loadProcurement's bulk answers
  // rehydration rather than a genuine edit - see suppressConsistencyClearRef.
  useEffect(() => {
    if (suppressConsistencyClearRef.current) {
      suppressConsistencyClearRef.current = false;
      return;
    }
    setConsistencyValidation(null);
  }, [answers]);

  // Clear a stale backend line-item rejection (BACKLOG.md Batch 1) as soon
  // as lineItems changes - any edit deserves a fresh check, and the live
  // client-side validation in LineItemsEditor will immediately re-flag the
  // same issue if it's still there.
  useEffect(() => {
    setLineItemErrors(null);
  }, [lineItems]);

  // Reset scroll position on every step change so each step starts at the top.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentStep]);

  // Derived state
  const getProcurementType = useCallback(() => (
    resolveProcurementType({ docProcurementType, refinedRequirement, parsedRequirement })
  ), [docProcurementType, refinedRequirement, parsedRequirement]);

  // PHASE 1 location fix (Ashok ruling, 2026-08-05): the buyer's ANSWER
  // wins; the agent's buyer_input.ship_to_location is the fallback used
  // only when no answer exists. The preference used to run the other way,
  // which meant an unvalidated free-text emission outranked a value that
  // had passed the location-master membership check at StepAnswers.jsx:
  // 568-585 - in prod that shipped "London Office - Site Code: GUR003"
  // (PROC-000045) and "HYD001 (London office)" (PROC-000046) onto every
  // line. Mirrors resolve_requirement_ship_to (routes/
  // _location_resolution.py), which applies the same precedence for the
  // requirement-level value the .docx renders.
  const getShipToLocation = useCallback(() => {
    return answeredShipToLocation || refinedRequirement?.buyer_input?.ship_to_location || '';
  }, [answeredShipToLocation, refinedRequirement]);

  // Item 4c (2026-07-21): the assessed category - the baseline the
  // L1-divergence consistency warning compares each line against.
  //
  // CORRECTED (C1b-1a, 2026-08-14). This read refined_requirement.
  // buyer_input.spend_category and its comment called that field "ground
  // truth ... same field assess_risk already treats as authoritative".
  // BOTH HALVES OF THAT CLAIM WERE FALSE, and a later reader would have
  // acted on them:
  //   - canonical_category_code's docstring (routes/procurement.py) says
  //     the opposite in as many words - "NOT buyer_input.spend_category:
  //     that is a refinement-time snapshot, and with category_decision_
  //     source user_changed reachable it can diverge from the category the
  //     procurement actually landed on".
  //   - assess_risk stopped treating it as authoritative on 2026-08-10: it
  //     resolves through canonical_category_code and scores risk against
  //     the union of CONFIRMED LINE categories, not this header field.
  // Measured 2026-08-14: the snapshot disagreed with the resolved category
  // on 12.9% of prod and 21.4% of staging documents carrying both - so the
  // warning was firing, or staying silent, against the wrong baseline on
  // roughly one document in five. It is also absent far more often (31 of
  // 55 prod documents carry it; all 55 resolve a canonical code).
  const getAssessedSpendCategory = useCallback(() => {
    return canonicalCategory || null;
  }, [canonicalCategory]);

  const getSubmissionDeadlineAnswer = useCallback(() => {
    const idx = businessQuestions.findIndex(q => normalizeQuestion(q).field_key === 'submission_deadline');
    if (idx === -1) return '';
    return answers[`q${idx}`] || '';
  }, [businessQuestions, answers]);

  // Step handlers

  // =====================================================================
  // THE ONE CONTINUE (Ashok ruling, 2026-08-21)
  // =====================================================================
  // Nothing has reached the server before this runs. This is the whole
  // persistence sequence for a new request, in the ruled order:
  //
  //   1. draft-create  - the DB serial is assigned here
  //   2. attach        - each held file, to the id just created,
  //                      through the EXISTING upload endpoints unchanged
  //   3. parse         - the requirement together with what is attached
  //   4. read-only     - rendered from parsed_requirement, as today
  //
  // ONE LOCK ACROSS THE WHOLE SEQUENCE. `loading` is set once at the top
  // and cleared once in the finally, so the buyer sees a single generation
  // lock rather than three flashing in turn. StepIntake picks the stage
  // text off pendingAction, the same pattern StepLineItems uses.
  //
  // DRAFT-CREATE IS SKIPPED WHEN A DOCUMENT ALREADY EXISTS, and that is not
  // an optimisation - it is the resume case. A Continue whose parse failed
  // leaves a draft behind by design (invariant I3); resuming it lands here
  // again with procurementId set, and creating a second document would
  // orphan the first and burn a second serial.
  //
  // PARTIAL FAILURE - each stage leaves the screen and the database
  // agreeing, which is the property that matters more than any of them
  // succeeding:
  //   draft-create fails -> NOTHING persisted. The buyer keeps requirement
  //                         and files and stays on the screen.
  //   an ATTACH fails    -> the draft and the requirement SURVIVE. The
  //                         failed file is named, the others are kept and
  //                         stay held so Continue can retry them. The draft
  //                         is NOT rolled back: deleting a document to tidy
  //                         up a failed upload would discard the serial and
  //                         the requirement the buyer already committed.
  //   the PARSE fails    -> draft, requirement and files all survive
  //                         (invariant I3 - a failed parse never destroys a
  //                         prior successful understanding).
  const handleIntakeSubmit = useCallback(async () => {
    if (!userRequirement.trim()) {
      toastError('Please enter your procurement requirement');
      return;
    }

    setLoading(true);
    // Local, because setState is async and stages 2-4 need the id stage 1
    // just produced. Reading procurementId from state here would see the
    // previous render's value - null on a first Continue - and attach every
    // file to nothing.
    let activeId = procurementId;
    try {
      // ---- 1. DRAFT-CREATE ------------------------------------------
      if (!activeId) {
        try {
          const draft = await api.createDraft(userRequirement);
          activeId = draft.data.procurement_id;
          setProcurementId(activeId);
          setSerialId(draft.data.serial_id);  // the DB serial, allocated here
          // STALE QUOTA PANEL FIX (2026-07-28 ruling): draft-create
          // increments beta_procurement_count server-side, so the sidebar's
          // "Beta: N/10" must refresh at the moment the counter moves.
          onUsageChanged?.();
        } catch (error) {
          const detail = error?.response?.data?.detail;
          if (detail?.code === 'BETA_EXPIRED' || detail?.code === 'BETA_LIMIT_REACHED') {
            const mainMessage = detail.code === 'BETA_EXPIRED'
              ? 'Your beta access has expired (30-day limit reached).'
              : 'You have reached the beta limit of 10 procurements.';
            toastError(
              <span>
                {mainMessage} To request an extension or additional procurements, please contact{' '}
                <a href="mailto:ashokr@sahinta.com" className="underline font-medium">
                  ashokr@sahinta.com
                </a>
              </span>
            );
          } else {
            showGenerationError(error, 'Failed to start your request', () => handleIntakeSubmit());
          }
          // NOTHING WAS PERSISTED. Return before any attach or parse: the
          // requirement and the held files are untouched in state, so the
          // buyer is looking at exactly what the server does not have.
          return;
        }
      }

      // ---- 2. ATTACH ------------------------------------------------
      // Existing endpoints, unchanged. A failure here is reported per file
      // and does NOT abort the sequence: the draft is real, the requirement
      // is saved, and a parse without one attachment is a better outcome
      // than a buyer stranded on a screen whose state disagrees with the
      // database.
      const failedAttachments = [];

      if (heldBoqFile) {
        try {
          const uploaded = await api.uploadBoq(activeId, heldBoqFile);
          // The server's rows replace the preview's. They are identical by
          // construction (same parse, same ids - see routes/documents.py's
          // preview_boq), and taking the persisted copy means what is on
          // screen is what is stored rather than what was predicted.
          setBoqItems(uploaded.data.boq_items || []);
          setBoqSource(uploaded.data.boq_source || null);
          setHeldBoqFile(null);
        } catch (error) {
          failedAttachments.push(heldBoqFile.name);
        }
      }

      if (heldDocuments.length > 0) {
        const stillHeld = [];
        for (const held of heldDocuments) {
          try {
            const uploaded = await api.uploadDocument(activeId, held.file, held.document_type);
            setAttachedDocuments(prev => [...prev, {
              id: uploaded.data.document_id,
              filename: uploaded.data.filename,
              document_type: uploaded.data.document_type,
              uploaded_at: uploaded.data.uploaded_at,
              size: uploaded.data.size,
            }]);
          } catch (error) {
            failedAttachments.push(held.filename);
            stillHeld.push(held);
          }
        }
        // Only the failures stay held, so a retry re-sends exactly what did
        // not land - never a duplicate of what did.
        setHeldDocuments(stillHeld);
      }

      if (failedAttachments.length > 0) {
        toastError(
          `Could not attach ${failedAttachments.join(', ')}. Your requirement was saved` +
          ` and the other files were attached - you can try these again.`
        );
      }

      // ---- 3. PARSE -------------------------------------------------
      const response = await api.parseIntake(activeId, userRequirement);
      setParsedRequirement(response.data.parsed_requirement);
      setCategoryActiveResolvable(response.data.category_active_resolvable ?? null);
      setEchoMetadata(response.data.echo_metadata || null);
      // Micro-fix (completes a4090e5): adopt the intake-classified type
      // immediately - otherwise resolveProcurementType falls through to
      // detectProcurementType's category-keyword guess for this entire
      // session, until generate-questions eventually returns the real one.
      if (response.data.procurement_type) {
        setDocProcurementType(response.data.procurement_type);
      }
      clearStepDirty('intake');

      // ---- 4. READ-ONLY ---------------------------------------------
      // parsedRequirement is now set, which is the mode discriminator - the
      // step re-renders read-only on its own. Staying on step 0 is the
      // point: the parse result is presented HERE for confirmation, and the
      // category interstitial (if it fires) needs the buyer on this screen.
      setCurrentStep(0);
    } catch (error) {
      // The parse failed. The draft, the requirement and every attached
      // file survive - nothing here discards them, and nothing above
      // rolls anything back.
      showGenerationError(error, 'Failed to analyse your requirement', () => handleIntakeSubmit());
    } finally {
      setLoading(false);
    }
  }, [procurementId, userRequirement, heldBoqFile, heldDocuments,
      clearStepDirty, onUsageChanged]);

  // Intake edit gate (Issue Log item 2): editing the requirement text on an
  // already-completed procurement re-parses it in place (never creates a
  // new procurement) and stales everything downstream per
  // STEP_DEPENDENTS.intake. Unlike Answers, Intake IS machine-generated
  // (parsed_requirement), so - like Uploads/Questions/Review - it fully
  // regenerates rather than reconciling; user-added questions are NOT
  // preserved (decision recorded: a changed requirement can make them
  // irrelevant - see memory/DESIGN-wizard-invalidation.md).
  const handleIntakeEditSubmit = useCallback(async () => {
    if (!userRequirement.trim()) {
      toastError('Please enter your procurement requirement');
      return;
    }

    // THE SNAPSHOT IS AN OBJECT NOW, not a bare string. Intake owns the
    // uploads since the merge, so "did anything actually change" has to
    // cover the files as well as the text - a buyer who unlocked the step
    // purely to add a document must not be told "nothing to regenerate".
    // Reading `snapshot?.userRequirement` rather than `snapshot` is the
    // whole of the fix: comparing an object against a string is always
    // unequal, which would have forced a re-parse on every unlock.
    const snapshot = entrySnapshotsRef.current.intake || {};
    const attachmentsActuallyChanged =
      JSON.stringify({ attachedDocuments: snapshot.attachedDocuments, boqItems: snapshot.boqItems })
      !== JSON.stringify({ attachedDocuments, boqItems });
    const intakeActuallyChanged =
      snapshot.userRequirement !== userRequirement || attachmentsActuallyChanged;

    if (!intakeActuallyChanged) {
      lockStepEdit('intake');
      clearStepStale('intake');
      clearStepDirty('intake');
      toastInfo('Requirement unchanged — nothing to regenerate');
      setCurrentStep(0);
      return;
    }

    setLoading(true);
    try {
      const response = await api.updateIntake(procurementId, userRequirement);
      setParsedRequirement(response.data.parsed_requirement);
      setCategoryActiveResolvable(response.data.category_active_resolvable ?? null);
      setEchoMetadata(response.data.echo_metadata || null);
      // Micro-fix (completes a4090e5): same immediate adoption as
      // handleIntakeSubmit - a re-parse can change procurement_type just as
      // easily as a first-time intake.
      if (response.data.procurement_type) {
        setDocProcurementType(response.data.procurement_type);
      }

      postWizardEvent('intake', 'edited');
      markStepsStale(STEP_DEPENDENTS.intake);
      lockStepEdit('intake');
      clearStepStale('intake');
      clearStepDirty('intake');

      setCurrentStep(0);
      toastSuccess('Requirement updated — downstream steps will be regenerated');
    } catch (error) {
      showGenerationError(error, 'Failed to update requirement', () => handleIntakeEditSubmit());
    } finally {
      setLoading(false);
    }
    // attachedDocuments/boqItems joined the deps with the snapshot
    // comparison above. Omitting them would leave this closure reading the
    // arrays as they were when the handler was last created - a stale
    // closure that reports "nothing to regenerate" for a file the buyer
    // just attached.
  }, [procurementId, userRequirement, attachedDocuments, boqItems, postWizardEvent, markStepsStale, clearStepStale, clearStepDirty, lockStepEdit]);

  // Zero-floor category guard (PROC-000026 family): persists the buyer's
  // forced category selection via update-intake's category_override path -
  // bypasses intake_agent entirely (no re-parse, no staleness cascade;
  // this isn't an "edit" of the requirement, just confirming a category).
  //
  // decisionContext (added 2026-08-04, Case 1 change affordance) is
  // REQUIRED and has no default: this one handler now serves two distinct
  // buyer events - 'forced_low_signal' from the zero-floor gate and
  // 'voluntary_change' from the passive "change" link - and the caller
  // that rendered the control is the only party that knows which. The
  // backend cross-checks the declaration against stored state and 400s on
  // disagreement rather than picking a side.
  //
  // Still NO staleness cascade, on either context (deliberate, unchanged):
  // both events are reachable only from Uploads, where nothing downstream
  // has been generated yet. Changing a category once questions exist is a
  // re-parse - a separate feature, not this one.
  // STALENESS GATE (2026-09-15) - what the wizard does with the server's
  // refusal. A MASTER_REFERENCE_STALE or RISK_CATEGORY_ATTESTATION_REQUIRED
  // 422 carries the server's own advisory, adopted as-is so the advisory
  // surface shows the references that just blocked the buyer in this sitting
  // (a buyer who never leaves the wizard never re-reads GET). When the list
  // names the header, categoryActiveResolvable is set false from that same
  // server fact, so the Intake picker opens instead of leaving a buyer who was
  // refused with a Continue that can never succeed and no control to fix it.
  // Returns true when the error was one of these refusals.
  const absorbMasterReferenceRefusal = useCallback((error) => {
    const detail = error?.response?.data?.detail;
    const isFork = error?.response?.status === 422 && detail
      && typeof detail === 'object' && detail.code === RISK_CATEGORY_ATTESTATION_REQUIRED;
    if (!isMasterReferenceStale(error) && !isFork) return false;
    if (detail.advisory) setMasterReferenceAdvisory(detail.advisory);
    if (referencesIncludeHeader(detail.stale_references)) setCategoryActiveResolvable(false);
    return true;
  }, []);

  // A boundary that returned 200 passed the server's guard, so nothing the
  // guard reads was stale at that moment - a stale advisory from earlier in
  // the session is cleared rather than left describing a fixed problem. NOT
  // called after assess-risk: the as-issued arm passes while stale.
  //
  // THE DATE HALF IS NOT CLEARED HERE (2026-09-17). Passing the master guard
  // says nothing about dates; `date_stops` is replaced only by a server
  // re-evaluation (adoptDateStops).
  const markMasterReferencesCurrent = useCallback(() => {
    setMasterReferenceAdvisory((prev) => (
      prev && prev.status === ADVISORY_STALE
        ? { status: 'current', stale_references: [], date_stops: prev.date_stops || [] }
        : prev
    ));
  }, []);

  // THE ADVISORY'S DATE HALF, re-evaluated by the server (2026-09-17). Adopted
  // from submit-answers and finalize-questions responses and from a
  // TEMPORAL_BLOCK refusal, so the banner empties as the buyer fixes dates
  // rather than only on a reload. Never computed here: which dates count
  // mirrors generate_rfp's choice, which turns on a refinement hash the
  // client cannot compute. A response without the field changes nothing.
  const adoptDateStops = useCallback((stops) => {
    if (!Array.isArray(stops)) return;
    setMasterReferenceAdvisory((prev) => ({
      ...(prev || { status: 'current', stale_references: [] }),
      date_stops: stops,
    }));
  }, []);

  // THE CASCADE (Ashok ruling, option B, 2026-09-17). With `cascade`, a
  // replaced RETIRED category marks STEP_DEPENDENTS.intake stale - the same
  // call, and the same list, as handleIntakeEditSubmit: ONE mechanism, not a
  // second. The caller (StepIntake) passes `cascade` only after the buyer has
  // confirmed; this handler additionally refuses to cascade any other decision
  // context, so an ordinary change can never reset downstream (mutant BV4).
  //
  // ORDER: WRITE, THEN MARK. Staleness is marked only after update-intake has
  // succeeded and its response is adopted; a failed write marks nothing
  // (mutant BV6). Returns { ok } for the caller.
  const handleCategoryOverride = useCallback(async (categoryCode, decisionContext, { cascade = false } = {}) => {
    try {
      const response = await api.updateIntake(procurementId, userRequirement, categoryCode, decisionContext);
      setParsedRequirement(response.data.parsed_requirement);
      setCategoryActiveResolvable(response.data.category_active_resolvable ?? null);
      // The header was just reselected and the server says it resolves: the
      // advisory's HEADER entry no longer describes the document. Any line
      // entries stay - they are still true until a boundary says otherwise.
      if (response.data.category_active_resolvable === true) {
        setMasterReferenceAdvisory((prev) => {
          if (!prev || prev.status !== ADVISORY_STALE) return prev;
          const rest = (prev.stale_references || []).filter((r) => r.field !== HEADER_CATEGORY_FIELD);
          return rest.length
            ? { ...prev, stale_references: rest }
            : { status: 'current', stale_references: [], date_stops: prev.date_stops || [] };
        });
      }
      const cascaded = cascade && decisionContext === RETIRED_CATEGORY_REPLACED;
      if (cascaded) {
        markStepsStale(STEP_DEPENDENTS.intake);
      }
      postWizardEvent('intake', 'category_confirmed', {
        category: categoryCode, decision_context: decisionContext, downstream_marked_stale: cascaded,
      });
      return { ok: true };
    } catch (error) {
      toastError(error.response?.data?.detail || 'Failed to save the selected category');
      return { ok: false, error };
    }
  }, [procurementId, userRequirement, postWizardEvent, markStepsStale]);

  // HEADER NO-FIT (F1 / C1b-1b, A2). Sibling of handleCategoryOverride
  // above and deliberately NOT folded into it: that handler's whole job is
  // to record WHICH category the buyer chose, and this one records that
  // they chose none. Sharing it would mean a `category` argument that is
  // sometimes a code and sometimes a request sentence.
  //
  // The response is adopted the same way, so the reopened Intake gate and
  // the stored document agree without a reload - parsedRequirement comes
  // back carrying the record, and category_active_resolvable comes back
  // false because the decided category was just cleared.
  const handleCategoryDeclareNoFit = useCallback(async (requestedText) => {
    try {
      const response = await api.declareHeaderCategoryNoFit(
        procurementId, userRequirement, requestedText,
      );
      setParsedRequirement(response.data.parsed_requirement);
      setCategoryActiveResolvable(response.data.category_active_resolvable ?? null);
      postWizardEvent('intake', 'category_no_fit_declared', { subject: 'header' });
    } catch (error) {
      toastError(
        error.response?.data?.detail || 'Failed to record the category request',
      );
    }
  }, [procurementId, userRequirement, postWizardEvent]);

  const handleUploadsComplete = useCallback(async () => {
    // Captured before the request (spec P2 §4.4 pattern, extended to
    // Uploads/Questions per E1): an edit-unlock revisit whose content
    // hasn't actually changed vs. entry must not stale anything downstream.
    // 'intake' since the merge - the uploads step no longer exists, so its
    // unlock bookkeeping moved onto the step that absorbed it. The snapshot
    // now covers the requirement text as well as the files (see
    // handleIntakeEditSubmit), so the comparison reads the same two keys
    // rather than the whole object.
    const wasUnlockedRevisit = stepEditUnlocked.has('intake');
    const intakeSnapshot = entrySnapshotsRef.current.intake || {};
    const uploadsActuallyChanged = wasUnlockedRevisit
      && JSON.stringify({ attachedDocuments: intakeSnapshot.attachedDocuments, boqItems: intakeSnapshot.boqItems })
         !== JSON.stringify({ attachedDocuments, boqItems });
    // 'questions' own staleness, captured before this call regenerates it -
    // gates the "regenerated because Uploads changed" notice so a first-
    // ever pass (always a real generation, never a "regeneration") never
    // shows it.
    const questionsWasStale = stepStates.questions === 'stale';

    setLoading(true);
    try {
      const questionsResponse = await api.generateQuestions(procurementId);
      const regenerated = Boolean(questionsResponse.data.regenerated);
      const newBusinessQuestions = questionsResponse.data.business_questions || [];
      setBusinessQuestions(newBusinessQuestions);
      setVendorQuestions(questionsResponse.data.vendor_risk_questions || []);
      // PROC-000054 fix: adopt the procurement_type the backend's generation
      // contract just verified this question set against, immediately - so
      // the read-time missing-field-key check (which runs off state, not a
      // reload) judges the new set against the SAME type it was generated
      // for, instead of whatever stale refined_requirement/category guess
      // was in state before this regeneration.
      if (questionsResponse.data.procurement_type) {
        setDocProcurementType(questionsResponse.data.procurement_type);
      }

      // Answers staleness semantics (spec §4): Answers is user-authored, so
      // a Questions regeneration reconciles the answers map by content
      // rather than "regenerating" it.
      setAnswers(prev => reconcileAnswersForQuestions(businessQuestions, newBusinessQuestions, prev));

      if (boqItems.length > 0) {
        const prefilledItems = boqItems.map((item, idx) => ({
          line_number: item.line_number || idx + 1,
          description: `${item.product_name}${item.description ? ' - ' + item.description : ''}`,
          // A BLANK BOQ QUANTITY STAYS BLANK (Ashok ruling, 2026-08-19).
          // This was `item.quantity || 1`, and it is the LAST hop before
          // the buyer sees the line - so it defeated the whole ruling on
          // the one path every buyer actually walks. The backend stops
          // inventing a quantity at parse time; if this re-invented it
          // here, the blank would never once reach the screen where it is
          // meant to be confirmed, and every backend test would stay
          // green. Found by the RULE 0 consumer sweep, not by the trace.
          //
          // `|| 1` also swallowed a legitimate 0, which is a different
          // wrong answer to the same question - ?? keeps whatever the
          // parser resolved and blanks only what it could not.
          // null, NOT '': this value is submitted verbatim to finalize-
          // line-items, and RequirementLineItem.quantity is Optional[float]
          // - '' fails Pydantic's own coercion and would surface FastAPI's
          // generic request-validation 422 instead of validate_line_items'
          // actionable per-line "Line N needs a quantity greater than 0".
          // The INPUT renders it as '' (LineItemsEditor's `?? ''`); the
          // DATA stays null.
          quantity: item.quantity ?? null,
          uom: item.uom || 'Each',
          need_by_date: null,
          start_date: null,
          end_date: null,
          ship_to_location: '',
          spend_category: '',
          // Provenance (spec P2 §5/§10.3): pre-filled from an uploaded BOQ.
          source: 'boq',
          modified: false
        }));
        setLineItems(prefilledItems);
      }

      if (wasUnlockedRevisit) {
        if (uploadsActuallyChanged) {
          postWizardEvent('intake', 'edited');
          markStepsStale(STEP_DEPENDENTS.intake);
        }
        lockStepEdit('intake');
      }

      // PROC-000062 fix 2: generate-questions now has an input-hash
      // short-circuit (same shape as finalize-questions §4.2) - 'questions'
      // is resolved either way (freshly regenerated or confirmed
      // unchanged), but the "regenerated because X changed" notice must
      // only fire when the backend confirms an actual change; a hash-hit
      // means nothing was actually replaced, however stale it looked a
      // moment ago.
      clearStepStale('questions');
      if (regenerated && questionsWasStale) {
        // 'questions' can go stale from an Uploads edit OR a cascade from an
        // upstream Intake edit (STEP_DEPENDENTS.intake) - only attribute the
        // notice to Uploads when this call's own edit is what caused it.
        setRegeneratedNotice({
          stepId: 'questions',
          message: uploadsActuallyChanged
            ? 'Questions regenerated because the attachments changed — review any customizations.'
            : 'Questions regenerated because the requirement changed — review any customizations.'
        });
      } else {
        // Hash-hit: don't let a notice from an earlier real regeneration
        // linger and misleadingly reappear on this no-op pass.
        setRegeneratedNotice(prev => (prev?.stepId === 'questions' ? null : prev));
      }

      markMasterReferencesCurrent();
      setCurrentStep(1);
      if (regenerated) {
        toastSuccess('Questions generated successfully');
      } else {
        // Skip-path toast: a hash-hit means no agent call happened at all -
        // tell the user explicitly rather than staying silent (which would
        // be indistinguishable from "nothing to report" on a real run).
        toastInfo('Questions are up to date — no regeneration needed');
      }
    } catch (error) {
      absorbMasterReferenceRefusal(error);
      showGenerationError(error, 'Failed to generate questions', () => handleUploadsComplete(), { totalLines: finalLineCount });
    } finally {
      setLoading(false);
    }
  }, [procurementId, boqItems, attachedDocuments, businessQuestions, stepEditUnlocked, stepStates.questions, postWizardEvent, markStepsStale, clearStepStale, lockStepEdit, absorbMasterReferenceRefusal, markMasterReferencesCurrent, finalLineCount]);

  const handleProceedToAnswers = useCallback(() => {
    const validQuestions = businessQuestions.filter(q => normalizeQuestion(q).question.trim());

    // Captured before the request - see handleUploadsComplete for the
    // matching pattern.
    const wasUnlockedRevisit = stepEditUnlocked.has('questions');
    const snapshot = entrySnapshotsRef.current.questions;
    const questionsActuallyChanged = wasUnlockedRevisit
      && JSON.stringify(snapshot) !== JSON.stringify(validQuestions);

    if (validQuestions.length !== businessQuestions.length) {
      setBusinessQuestions(validQuestions);
    }

    // Reconcile by content against the entry snapshot on a genuine
    // unlocked edit, or against the pre-filter list otherwise (handles the
    // empty-question-filter case the same way, uniformly).
    const baselineQuestions = wasUnlockedRevisit ? snapshot : businessQuestions;
    setAnswers(prev => reconcileAnswersForQuestions(baselineQuestions, validQuestions, prev));

    if (wasUnlockedRevisit) {
      if (questionsActuallyChanged) {
        postWizardEvent('questions', 'edited');
        markStepsStale(STEP_DEPENDENTS.questions);
      }
      lockStepEdit('questions');
    }

    clearStepStale('questions');
    setCurrentStep(2);
  }, [businessQuestions, stepEditUnlocked, postWizardEvent, markStepsStale, clearStepStale, lockStepEdit]);

  const handleAnswersSubmit = useCallback(async () => {
    const hasAnswers = Object.values(answers).some(a => a && a.toString().trim());
    if (!hasAnswers) {
      toastError('Please provide at least one answer');
      return;
    }
    
    // PROMPT COPY FIX batch, DEFECT 1 (2026-07-26): a legacy, pre-field_key
    // mandatory-field scanner used to live here - it matched question TEXT
    // against bare keywords ("date", "when", "timeline", "quantity",
    // "currency"...) with NO regard for field_key, is_mandatory, or
    // procurement_type, and produced an UNCODED toast ("Please provide
    // required information: Date/Timeline"). That made it a FOURTH
    // mandatory-enforcement site outside the coded framework (the other
    // three - the field_key backfill, the generation contract, and
    // StepAnswers.jsx's own field_key-driven mandatory-group checks - were
    // all already type-conditional) - it wrongly blocked a MIXED
    // requirement's correctly-optional execution-window question ("When
    // should the installation/service work be performed?") purely because
    // its text contains "when"/a date reference, regardless of that
    // question's real is_mandatory: false. Deleted rather than recoded:
    // everything it guarded (location/date/quantity/currency presence) is
    // already checked, correctly and type-conditionally, by
    // StepAnswers.jsx's own field_key-driven checks BEFORE this function is
    // ever called (ship_to_location presence, the type-appropriate required
    // date field_key(s), quantity, rfp_currency) - single-source validation,
    // per the standing "every blocking message carries a rule code, and
    // there is exactly one field_key-driven mandatory-check path" rule.
    const questionsList = businessQuestions || [];

    const extractedCurrency = extractCurrencyAnswer(questionsList, answers);
    if (extractedCurrency) {
      setSelectedCurrency(extractedCurrency);
    }
    const extractedLocation = extractLocationAnswer(questionsList, answers);
    if (extractedLocation) {
      setAnsweredShipToLocation(extractedLocation);
    }

    // Captured before the request: an edit-unlock revisit whose content
    // hasn't actually changed vs. entry must not stale anything downstream
    // (spec P2 §4.4 "no ambers stick, all skips").
    const wasUnlockedRevisit = stepEditUnlocked.has('answers');
    const answersActuallyChanged = wasUnlockedRevisit
      && JSON.stringify(entrySnapshotsRef.current.answers) !== JSON.stringify(answers);

    setLoading(true);
    try {
      // businessQuestions passed alongside: these answers are numbered
      // against THIS list, and it may differ from what the server has
      // stored (question edits are client-only until finalize). See
      // api.submitAnswers.
      const response = await api.submitAnswers(procurementId, answers, businessQuestions);
      // Persisted server-side as of this line regardless of which branch
      // follows (blocking-error / warning-confirm / straight-through) - the
      // dirty flag must clear here, not gated behind setCurrentStep(3).
      clearStepDirty('answers');
      markMasterReferencesCurrent();
      adoptDateStops(response.data.date_stops);
      const revised = response.data.revised_vendor_questions || [];
      // F5: the CONTINUE route (the common one - a buyer in a single sitting
      // never calls GET). Both arrays are split through the same predicate so
      // StepReview's index pairing of revisedVendorQuestions[i] against
      // editableQuestions[i] still lines up; filtering only one would attach
      // the "Revised" badge to the wrong question.
      setRevisedVendorQuestions(keptEntries(revised));
      setRemovedVendorQuestions(removedEntries(revised));
      setRevisionSummary(response.data.revision_summary || '');

      const editable = keptEntries(revised).map(q => q.revised || q.original);
      setEditableQuestions(editable);

      // Handle consistency check results
      const consistencyCheck = response.data.consistency_check;
      if (consistencyCheck) {
        setConsistencyValidation(consistencyCheck);

        // Check for blocking errors - the submission is rejected, so the
        // step must stay unlocked/editable for the user to fix it. Neither
        // the 'edited' event nor any staleness marking should fire yet.
        if (response.data.has_blocking_errors) {
          const errorIssues = consistencyCheck.issues?.filter(i => i.severity === 'error') || [];
          toastError(`Cannot proceed: ${errorIssues.length} validation error(s) found. Please correct your answers.`, {
            duration: 6000
          });
          // Don't proceed - stay on answers step but show validation issues
          return;
        }
      }

      // Past the blocking-errors gate: this submission is accepted, whether
      // advancing immediately below or (on a warning) waiting for explicit
      // confirmation via confirmAdvanceToLineItems.
      if (wasUnlockedRevisit) {
        if (answersActuallyChanged) {
          postWizardEvent('answers', 'edited');
          markStepsStale(STEP_DEPENDENTS.answers);
        }
        lockStepEdit('answers');
      }

      // Answers staleness semantics (spec §4): unlike Items/RFP/Risk,
      // Answers doesn't "regenerate" - a stale Answers auto-unlocks for
      // editing (isStepPreviouslyCompleted already excludes stale steps
      // from read-only) and is resolved the moment its own submission is
      // accepted, whether that stale mark came from a direct edit-unlock
      // above or a Questions/Uploads cascade that reconciled the answers
      // map upstream.
      clearStepStale('answers');

      if (consistencyCheck) {
        // Non-blocking warnings still require an explicit user choice - don't
        // auto-advance here. StepAnswers shows a blocking dialog (driven by
        // consistencyValidation) and calls confirmAdvanceToLineItems() once
        // the user chooses to proceed.
        const warningIssues = consistencyCheck.issues?.filter(i => i.severity === 'warning') || [];
        if (warningIssues.length > 0) {
          return;
        }
      }

      setCurrentStep(3);
    } catch (error) {
      // The backend persists user_answers before running the revision agent
      // (item 1), so a 502 here never loses what was typed - Retry simply
      // resubmits the current answers. A staleness refusal is placed AFTER
      // that write too (ruling 2), so the typed answers are kept there as
      // well - but the step does not advance, and Retry is withheld.
      absorbMasterReferenceRefusal(error);
      showGenerationError(error, 'Failed to process answers', () => handleAnswersSubmit(), { totalLines: finalLineCount });
    } finally {
      setLoading(false);
    }
  }, [procurementId, answers, businessQuestions, stepEditUnlocked, postWizardEvent, markStepsStale, clearStepStale, clearStepDirty, lockStepEdit, absorbMasterReferenceRefusal, markMasterReferencesCurrent, adoptDateStops, finalLineCount]);

  const confirmAdvanceToLineItems = useCallback(() => {
    setCurrentStep(3);
  }, []);

  // RETURNS AN OUTCOME (2026-09-17): { ok: true } or { ok: false, error,
  // message }. The Lines step's "Regenerate from answers" renders a refusal
  // beside its own button from this; Review's Continue ignores it. The toast
  // below is unchanged.
  const handleFinalizeQuestions = useCallback(async (force = false) => {
    if (editableQuestions.length === 0) {
      toastError('Please add at least one vendor question');
      return { ok: false, error: null, message: 'Please add at least one vendor question' };
    }

    // Captured before the request (spec P2 §4.4 "no ambers stick" on a
    // no-op edit-unlock revisit) and before this call's own staleness
    // bookkeeping mutates state.
    const wasUnlockedRevisit = stepEditUnlocked.has('review');
    const reviewActuallyChanged = wasUnlockedRevisit
      && JSON.stringify(entrySnapshotsRef.current.review) !== JSON.stringify(editableQuestions);
    const itemsNeedsFreshApply = stepStates.items !== 'completed';
    // Narrower than itemsNeedsFreshApply: 'stale' means Lines HAD a prior
    // artifact that's now being replaced because something upstream
    // changed - that's the only case the notice should ever describe.
    // 'locked' (first-ever generation, no prior artifact) must never be
    // reported as a "regeneration" regardless of what the backend's
    // regenerated flag says - on a first pass it's always true, but
    // nothing was actually replaced.
    const itemsWasStale = stepStates.items === 'stale';

    setLoading(true);
    try {
      const finalQuestions = editableQuestions.filter(q => q.trim());
      const finalizeResponse = await api.finalizeQuestions(procurementId, finalQuestions, businessQuestions, force);
      const regenerated = Boolean(finalizeResponse.data.regenerated);
      markMasterReferencesCurrent();
      adoptDateStops(finalizeResponse.data.date_stops);

      // finalize-questions is the forward-persist for BOTH business
      // questions (Questions step) and vendor questions (Review step) -
      // both are written to the doc in this one call, so both clear here.
      clearStepDirty('questions');
      clearStepDirty('review');

      setRefinedRequirement(finalizeResponse.data.refined_requirement);
      // C1b-1a: the fresh-session path onto Lines. loadProcurement below
      // is the RESUME path only (its one caller is ContinueProcurement.
      // jsx), so setting canonicalCategory there alone would leave it null
      // for every buyer who reaches Lines without ever leaving the wizard.
      setCanonicalCategory(finalizeResponse.data.canonical_category || null);

      if (finalizeResponse.data.consistency_validation) {
        setConsistencyValidation(finalizeResponse.data.consistency_validation);
        const validation = finalizeResponse.data.consistency_validation;
        if (!validation.is_consistent && validation.issues?.length > 0) {
          const errorCount = validation.issues.filter(i => i.severity === 'error').length;
          const warningCount = validation.issues.filter(i => i.severity === 'warning').length;
          if (errorCount > 0) {
            toastError(`Found ${errorCount} consistency error(s) - please review and correct`);
          } else if (warningCount > 0) {
            toastWarning(`Found ${warningCount} consistency warning(s) - please review`);
          }
        }
      }

      const responseItems = finalizeResponse.data.requirement_line_items ||
                    finalizeResponse.data.refined_requirement?.requirement_line_items || [];
      setRequirementLineItems(responseItems);

      // Backfill ship_to_location wherever the LLM (or BOQ prefill) left it blank,
      // using the buyer's answer - applied uniformly whether this is the first time
      // line items are populated (no-BOQ path) or a BOQ-prefilled array (BOQ path).
      //
      // PHASE 1 location fix (Ashok ruling, 2026-08-05): ANSWER FIRST. This
      // read used to prefer refined_requirement.buyer_input.ship_to_location,
      // so the agent's unvalidated string was stamped onto every blank line -
      // and BOQ prefill guarantees every line starts blank (:737), which is
      // how one prose emission reached all 9 rows of PROC-000045. Same
      // precedence as getShipToLocation above and as the backend resolver.
      const backfillLocation = answeredShipToLocation || finalizeResponse.data.refined_requirement?.buyer_input?.ship_to_location;

      // RESOLVABILITY BACKFILL (Ashok ruling, 2026-08-05) - replaces the
      // emptiness discriminator, which was inert: the agent fills every
      // line's ship_to_location from refinement_agent.txt:123, so no line
      // is ever blank and "backfill the blank ones" never fired (staging
      // PROC-000308). A line now takes the resolved answer iff its current
      // value does not resolve against location master; see
      // lib/lineItemLocationBackfill.js for the rule and its fail-safe.
      //
      // The master list is fetched HERE rather than read from component
      // state deliberately: a state-backed list can still be in flight
      // when this handler runs, which would make the fail-safe fire on
      // fetch timing rather than on fact - the backfill would apply or not
      // depending on a race. Awaiting makes the decision deterministic,
      // and a failed or empty fetch still lands on the fail-safe.
      let masterLocations = [];
      try {
        const locationsResponse = await api.getLocations();
        masterLocations = locationsResponse.data.locations || [];
      } catch (locationsError) {
        // Swallowed on purpose: an unreachable master list must not fail
        // the finalize the buyer actually asked for. It degrades to the
        // fail-safe (no overwrite), same as an empty list.
        masterLocations = [];
      }

      // G1 fix (spec P2 §5): whenever Lines hasn't settled as 'completed'
      // (i.e. it's 'locked' - first time - or 'stale' - upstream changed),
      // apply the fresh line items, replacing whatever's local. Only when
      // Lines is already 'completed' and untouched by this change do we
      // keep the user's local edits as-is. Either way the overwrite
      // decision itself lives in one place, above.
      //
      // A FORCED REGENERATION ALWAYS APPLIES (Ashok ruling, 2026-09-17).
      // "Regenerate from answers" is the buyer saying "replace my lines" - its
      // confirmation says so. On a resumed rfp_generated document Lines reads
      // 'completed', so without this the fresh lines were written to the
      // server and never reached the editor: a regeneration that appeared to
      // do nothing, which is how PROC-000355 started.
      if ((itemsNeedsFreshApply || force) && responseItems.length > 0) {
        const tagged = responseItems.map(item => ({ ...item, source: item.source || 'ai', modified: false }));
        setLineItems(applyResolvedLocationToLines(tagged, backfillLocation, masterLocations));
      } else if (!itemsNeedsFreshApply && !force) {
        setLineItems(prev => applyResolvedLocationToLines(prev, backfillLocation, masterLocations));
      }

      if (wasUnlockedRevisit) {
        if (reviewActuallyChanged) {
          postWizardEvent('review', 'edited');
        }
        lockStepEdit('review');
      }

      // 'review' is resolved either way (freshly regenerated or confirmed
      // unchanged); its dependents only need re-marking when the backend
      // confirms an actual change - self-healing makes this safe to do
      // unconditionally: 'items' gets cleared immediately below, and
      // 'rfp'/'risk' stay invisible (locked) until their own handlers run.
      clearStepStale('review');
      if (regenerated) {
        markStepsStale(['items', 'rfp', 'risk']);
        if (itemsWasStale) {
          setRegeneratedNotice({ stepId: 'items', causeLabel: 'Review' });
        }
      } else {
        // Hash-hit: don't let a notice from an earlier real regeneration
        // linger and misleadingly reappear on this no-op pass.
        setRegeneratedNotice(prev => (prev?.stepId === 'items' ? null : prev));
      }
      clearStepStale('items');

      // Skip-path toast: a hash-hit means no agent call happened at all -
      // tell the user explicitly rather than staying silent (which would
      // be indistinguishable from "nothing to report" on a real run).
      if (!regenerated) {
        toastInfo('Line items are up to date — no regeneration needed');
      }

      setCurrentStep(4);
      return { ok: true };
    } catch (error) {
      // A staleness refusal here does NOT navigate, so editableQuestions and
      // businessQuestions - the buyer's edits, which the server persists only
      // in the same write as the refinement (R4) - stay in state and on screen.
      absorbMasterReferenceRefusal(error);
      showGenerationError(error, 'Failed to finalize questions', () => handleFinalizeQuestions(force), { totalLines: finalLineCount });
      return { ok: false, error };
    } finally {
      setLoading(false);
    }
  }, [procurementId, editableQuestions, businessQuestions, answeredShipToLocation, stepEditUnlocked, stepStates.items, postWizardEvent, markStepsStale, clearStepStale, clearStepDirty, lockStepEdit, absorbMasterReferenceRefusal, markMasterReferencesCurrent, adoptDateStops, finalLineCount]);

  // Lines step extras (spec P2 §4.4, independent of staleness):
  // "Restore AI-generated lines" - re-applies the stored AI baseline with
  // no LLM call at all (fixes G2 - recovery no longer depends on an LLM
  // re-roll after deleting all lines).
  const handleRestoreAiLines = useCallback(() => {
    const restored = requirementLineItems.map(item => ({ ...item, source: 'ai', modified: false }));
    setLineItems(restored);
    // Restoring the AI baseline is itself a resolution of any pending
    // staleness on Lines (e.g. cascaded from an earlier Answers edit) - it
    // must clear the amber the same way a real regeneration through Review
    // does, or the step indicator would stay stuck stale indefinitely.
    clearStepStale('items');
    // The restored set is local state that diverges from whatever's
    // persisted until finalize-line-items runs again - same unsaved-changes
    // risk as any other line edit.
    markStepDirty('items');
    postWizardEvent('items', 'restored');
  }, [requirementLineItems, postWizardEvent, clearStepStale, markStepDirty]);

  const handleFinalizeLineItems = useCallback(async () => {
    if (lineItems.length === 0) {
      toastError('Please add at least one line item');
      return;
    }
    
    if (!selectedCurrency) {
      toastError('Please select a currency for vendor quotations');
      return;
    }

    // Captured before the request - see the matching itemsWasStale note in
    // handleFinalizeQuestions: 'stale' means RFP HAD a prior artifact being
    // replaced; 'locked' (first-ever generation) must never be reported as
    // a regeneration even though the backend's regenerated flag is true on
    // a first pass too.
    const rfpWasStale = stepStates.rfp === 'stale';

    setLoading(true);
    try {
      // Pass currency with line items
      await api.finalizeLineItems(procurementId, lineItems, selectedCurrency);
      setFinalLineCount(lineItems.length);
      clearStepDirty('items');
      setLineItemErrors(null);
      const rfpResponse = await api.generateRfp(procurementId);
      markMasterReferencesCurrent();
      // generate_rfp's date gate sits above both of its arms and reads the
      // same dates the advisory does, so a 200 means none of them is past.
      adoptDateStops([]);
      setRfpDocument(rfpResponse.data.rfp_document);
      setRfpVersion(rfpResponse.data.rfp_version);
      // STALE QUOTA PANEL FIX (2026-07-28 ruling): Credits (usage_limit)
      // decrements server-side on an actual generation run (never on the
      // input-hash-unchanged skip path, which returns before reaching the
      // decrement) - refresh unconditionally either way, since a
      // no-op-skip refresh just re-confirms the same value and costs one
      // cheap GET. See handleIntakeSubmit's own onUsageChanged call for
      // the other half of the same panel (Beta: N/10, which moves on
      // intake instead - a DIFFERENT event from this one; the two are not
      // the same counter, see PROD-DB-CHECKLIST/report notes).
      onUsageChanged?.();

      // Lines is always directly editable (no read-only lock), so there's
      // no client-side "did it change" heuristic to gate on here - the
      // hash-miss/hit truth from generate-rfp itself is authoritative.
      // Self-healing: 'risk' only becomes visible once actually reached.
      clearStepStale('items');
      clearStepStale('rfp');
      if (rfpResponse.data.regenerated) {
        markStepsStale(['risk']);
        if (rfpWasStale) {
          setRegeneratedNotice({ stepId: 'rfp', causeLabel: 'Line Items' });
        }
      } else {
        setRegeneratedNotice(prev => (prev?.stepId === 'rfp' ? null : prev));
      }

      setCurrentStep(5);
      if (rfpResponse.data.regenerated) {
        toastSuccess('RFP generated successfully');
      } else {
        toastInfo('RFP is up to date — no regeneration needed');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      // Staleness refusal: adopt the server's advisory; the toast below comes
      // from showGenerationError (a dict detail fails the line-item shape).
      absorbMasterReferenceRefusal(error);

      // Item 7 trigger (b) - HARD backstop at the RFP-generation gate
      // (answers-step batch, 2026-07-22): routes/_answers_validation.py
      // mirrors this same field_key/temporal check server-side and runs it
      // unconditionally on every generate-rfp call. Route back to Answers
      // with the same explaining-dialog/blocking-banner treatment as the
      // resume-time hard case (loadProcurement's temporalBlock, above).
      if (error?.response?.status === 400 && detail?.code === 'TEMPORAL_BLOCK') {
        // `rule_code` (HVPT/HVND/etc) is distinct from the outer "code"
        // discriminator ("TEMPORAL_BLOCK") - carried through so StepAnswers
        // can pick the right narrative frame (item 3: genuinely-past-date
        // vs relationship-rule) and so the toast can append the suffix.
        setTemporalBlock({ field: detail.field, message: detail.message, code: detail.rule_code });
        adoptDateStops(detail.date_stops);
        // KNOWN DISAGREEMENT (recorded 2026-09-17, not fixed - a boundary
        // behaviour this slice may not change): this always routes to
        // Answers, even when rule_code is a LINE's own HVND/HVSD
        // (detail.line_number set), which can only be fixed at Lines. The
        // advisory banner correctly owns that stop to the Lines step, so the
        // two surfaces point different ways for the same date.
        setCurrentStep(2);
        toastError(appendRuleCodeSuffix(detail.message, detail.rule_code));
        return;
      }

      // NO-FIT PARK (F1 / C1b-1b, A3b). Branched on the CODE, never on the
      // sentence: message text is presentation, and a consumer parsing it
      // re-derives a decision the producer already made. Same shape and
      // same placement as TEMPORAL_BLOCK above.
      //
      // NO RETRY AFFORDANCE, and that is the whole reason this branch
      // exists rather than letting the generic handler render the string.
      // showGenerationError offers Retry for anything it does not
      // recognise, and re-running changes nothing here - only the master
      // gaining the value, or the buyer reselecting, clears a park. A
      // button that can never succeed is worse than no button: it tells
      // the buyer the failure was transient when it is not.
      if (error?.response?.status === 422 && detail?.code === 'CATEGORY_NO_FIT_BLOCK') {
        toastError(detail.message);
        return;
      }

      // Shape-checked, not just Array.isArray (Item 0 hardening,
      // 2026-07-21): FastAPI's OWN request-body validation also returns a
      // 422 with an array `detail`, but shaped as {loc, msg, type} -
      // completely different from our hard-validation module's
      // {line_index, field, message}. Routing that shape into
      // lineItemErrors would silently populate a phantom, unrenderable
      // entry (no line/field to attach it to) while still counting toward
      // "Fix N line item issue(s)" - exactly the count-with-nothing-
      // visible defect class Item 0 exists to prevent.
      const isLineItemValidationDetail = Array.isArray(detail) && detail.every(
        (e) => e && typeof e.line_index === 'number' && typeof e.field === 'string'
      );
      if (error?.response?.status === 422 && isLineItemValidationDetail) {
        // Hard line-item validation rejection (BACKLOG.md Batch 1) - the
        // backend's authoritative backstop for a stale client. Rendered
        // against the matching lines by LineItemsEditor (via lineItemErrors
        // below), not the generic agent-failure toast/retry pattern.
        setLineItemErrors(detail);
        toastError('Some line items need to be corrected before the RFP can be generated.');
      } else {
        console.error('Generate RFP error:', error);
        console.error('Error response:', error.response?.data);
        showGenerationError(error, 'Failed to generate RFP', () => handleFinalizeLineItems(), { totalLines: finalLineCount });
      }
    } finally {
      setLoading(false);
    }
  }, [procurementId, lineItems, selectedCurrency, stepStates.rfp, clearStepStale, clearStepDirty, markStepsStale, onUsageChanged, absorbMasterReferenceRefusal, markMasterReferencesCurrent, adoptDateStops, finalLineCount]);

  const handleProceedToRisk = useCallback(async () => {
    // Captured before the request - see the matching itemsWasStale note in
    // handleFinalizeQuestions: 'stale' means Risk HAD a prior artifact
    // being replaced; 'locked' (first-ever generation) must never be
    // reported as a regeneration even though the backend's regenerated
    // flag is true on a first pass too.
    const riskWasStale = stepStates.risk === 'stale';

    setLoading(true);
    try {
      const response = await api.assessRisk(procurementId);
      setRiskAssessment(response.data.risk_assessment);

      clearStepStale('risk');
      if (response.data.regenerated) {
        if (riskWasStale) {
          setRegeneratedNotice({ stepId: 'risk', causeLabel: 'Line Items' });
        }
      } else {
        setRegeneratedNotice(prev => (prev?.stepId === 'risk' ? null : prev));
      }

      if (!response.data.regenerated) {
        toastInfo('Risk profile is up to date — no regeneration needed');
      }

      setCurrentStep(6);
      return true;
    } catch (error) {
      // THE RISK-STAGE FORK (staleness gate): not a failure - a question.
      // The dialog opens with the server's detail, no toast, and the
      // resolved value is null (neither success nor failure) so StepRisk
      // does not show its "failed to generate" state behind the dialog.
      const detail = error?.response?.data?.detail;
      if (absorbMasterReferenceRefusal(error)
          && detail?.code === RISK_CATEGORY_ATTESTATION_REQUIRED) {
        setRiskFork(detail);
        return null;
      }
      // riskAssessment is only set on success (line above), so a failure
      // here never leaves a stale/plausible-looking score on screen - see
      // StepRisk.jsx's isCompleted guard.
      showGenerationError(error, 'Failed to assess risk', () => handleProceedToRisk(), { totalLines: finalLineCount });
      // Returned (not thrown) so StepRisk's own Generate/Retry affordance
      // (Risk-step resting-state fix, 2026-07-21) can distinguish success
      // from failure without a try/catch around a handler that already
      // handles its own error - the sole other caller (StepRfpPreview's
      // "Generate Risk Intelligence" button) ignores the return value and
      // is unaffected; it already relied on the toast for failure feedback.
      return false;
    } finally {
      setLoading(false);
    }
  }, [procurementId, stepStates.risk, clearStepStale, absorbMasterReferenceRefusal, finalLineCount]);

  // THE BUYER'S ANSWER TO THE FORK. Every arm is recorded server-side first;
  // only then does the wizard act on it. The fork never edits the document -
  // `regenerate` just takes the buyer to the step that owns the retired
  // category (Intake for the header, Lines otherwise, read off the server's
  // references), where the ordinary gates apply.
  const handleRiskForkDecision = useCallback(async (decision) => {
    const fork = riskFork;
    setRiskForkBusy(true);
    try {
      await api.recordRiskCategoryAttestation(procurementId, decision);
    } catch (error) {
      setRiskForkBusy(false);
      showGenerationError(error, 'Failed to record your answer', () => handleRiskForkDecision(decision));
      return;
    }
    setRiskForkBusy(false);
    setRiskFork(null);
    const stepIndex = (id) => WIZARD_STEPS.findIndex((s) => s.id === id);
    if (decision === 'as_issued') {
      await handleProceedToRisk();
    } else if (decision === 'regenerate') {
      // Intake for a retired category header, Answers for a retired ship-to
      // header, Lines otherwise (2026-09-16: the fork covers every master).
      setCurrentStep(stepIndex(regenerateStepFor(fork?.stale_references)));
    } else {
      // cancel - back to the previous step (the RFP), without deciding.
      setCurrentStep(stepIndex('rfp'));
    }
  }, [procurementId, riskFork, handleProceedToRisk]);

  const handleComplete = useCallback(() => {
    toastSuccess('Procurement process completed!');
    navigate(`/procurement/${procurementId}`);
  }, [navigate, procurementId]);

  // Question editing helpers (Review step - vendor questions). Not
  // persisted until finalize-questions succeeds, same as business
  // questions below, so 'review' stays dirty across the whole Review ->
  // Line Items round trip until that call actually completes.
  const handleQuestionEdit = useCallback((index, value) => {
    setEditableQuestions(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    markStepDirty('review');
  }, [markStepDirty]);

  const handleAddQuestion = useCallback(() => {
    setEditableQuestions(prev => [...prev, '']);
    markStepDirty('review');
  }, [markStepDirty]);

  const handleRemoveQuestion = useCallback((index) => {
    setEditableQuestions(prev => prev.filter((_, i) => i !== index));
    markStepDirty('review');
  }, [markStepDirty]);

  // Business question helpers (Questions step). Edits here aren't
  // persisted until finalize-questions succeeds either (business_questions
  // is only written to the doc at that point - see handleFinalizeQuestions)
  // - not at handleProceedToAnswers, which is a pure client-side step
  // change with no API call.
  const handleAddBusinessQuestion = useCallback(() => {
    setBusinessQuestions(prev => [...prev, '']);
    setAnswers(prev => ({ ...prev, [`q${businessQuestions.length}`]: '' }));
    markStepDirty('questions');
  }, [businessQuestions.length, markStepDirty]);

  const handleEditBusinessQuestion = useCallback((index, value) => {
    setBusinessQuestions(prev => {
      const updated = [...prev];
      updated[index] = applyBusinessQuestionTextEdit(updated[index], value);
      return updated;
    });
    markStepDirty('questions');
  }, [markStepDirty]);

  const handleRemoveBusinessQuestion = useCallback((index) => {
    setBusinessQuestions(prev => prev.filter((_, idx) => idx !== index));
    setAnswers(prev => {
      const newAnswers = {};
      // Preserve non-positional keys (e.g. "Any other suggestion", Issue
      // 19) - unrelated to which numbered business question got removed.
      Object.keys(prev).forEach((key) => {
        if (!/^q\d+$/.test(key)) newAnswers[key] = prev[key];
      });
      businessQuestions.forEach((_, idx) => {
        if (idx < index) {
          newAnswers[`q${idx}`] = prev[`q${idx}`] || '';
        } else if (idx > index) {
          newAnswers[`q${idx - 1}`] = prev[`q${idx}`] || '';
        }
      });
      return newAnswers;
    });
    markStepDirty('questions');
  }, [businessQuestions, markStepDirty]);

  // Load existing procurement (for ContinueProcurement). `options.forceStep`
  // (2026-07-26, ProcurementDetail "Edit" button fix): lands the wizard on
  // a specific step index regardless of getStepFromStatus - used ONLY to
  // route the "Edit" trigger on the Original Requirement card to step 0
  // (Intake) instead of wherever the backend status would normally resume.
  // Deliberately does NOT touch furthestStepReached, which still reflects
  // the procurement's REAL progress (getStepFromStatus's own result) - the
  // whole point is that isStepPreviouslyCompleted('intake') stays true so
  // StepIntake renders its read-only view + ReadOnlyStepBanner Edit
  // trigger + confirmation dialog, the same gate a Back-navigated visit
  // would hit. This never unlocks a step or bypasses that dialog itself.
  const loadProcurement = useCallback(async (id, options = {}) => {
    try {
      setLoading(true);
      const response = await api.getProcurement(id);
      const data = response.data;
      
      setProcurementId(id);
      setSerialId(data.serial_id || null);  // Load serial ID
      setUserRequirement(data.user_requirement || '');
      setParsedRequirement(data.parsed_requirement || null);
      setCategoryActiveResolvable(data.category_active_resolvable ?? null);
      // THE RESUME ADVISORY - informs only; nothing below branches on it, and
      // the resume step is computed from status exactly as before.
      setMasterReferenceAdvisory(data.master_reference_advisory ?? null);
      setProcurementStatus(data.status ?? null);
      setFinalLineCount(Array.isArray(data.final_line_items) ? data.final_line_items.length : null);
      setRiskFork(null);
      setEchoMetadata(data.echo_metadata || null);
      setDocProcurementType(data.procurement_type || null);
      setBusinessQuestions(data.business_questions || []);
      setVendorQuestions(data.vendor_risk_questions || []);
      // Review-step revision summary and the Lines-step consistency banner
      // (G-3): both are produced mid-session (submit-answers /
      // finalize-questions) and were previously never restored on resume,
      // leaving those banners blank even though the underlying data exists.
      // F5: the RESUME route. Same split as the CONTINUE route above - the
      // removed set is restored too, so a buyer who resumes past Answers
      // still sees which questions were removed and why (ruling 3).
      setRevisedVendorQuestions(keptEntries(data.revised_vendor_questions || []));
      setRemovedVendorQuestions(removedEntries(data.revised_vendor_questions || []));
      setRevisionSummary(data.revision_summary || '');
      suppressConsistencyClearRef.current = true;
      setAnswers(data.user_answers || {});
      // PHASE 1 location fix (Ashok ruling, 2026-08-05) - REQUIRED for the
      // answer-first precedence above to hold past the live session.
      // answeredShipToLocation had exactly one setter (handleAnswersSubmit,
      // :862) and was never rehydrated here, so on every resume it was null
      // and both reads fell straight through to the agent's emission -
      // inverting the preference alone would have been a no-op for any
      // procurement continued in a later session. Derived from the SAME two
      // persisted fields, through the SAME extractor, as the live capture -
      // no new source, no new precedence (see DATAFLOW.md G-7, which
      // recorded this gap as "not addressed this cycle").
      setAnsweredShipToLocation(
        extractLocationAnswer(data.business_questions || [], data.user_answers || {})
      );
      // G-1: on a resume specifically dropped back onto Answers because of a
      // prior blocking error (status "answers_need_correction"), show that
      // blocking result (preliminary_consistency_check, submit-answers'
      // stage-specific field - previously write-only, G-11) rather than
      // whatever finalize-questions may have separately produced. Any other
      // status prefers the finalize-questions-stage result, matching what
      // was already shown live in that session.
      setConsistencyValidation(
        data.status === 'answers_need_correction'
          ? (data.preliminary_consistency_check || data.consistency_validation || null)
          : (data.consistency_validation || null)
      );
      setRefinedRequirement(data.refined_requirement || null);
      setCanonicalCategory(data.canonical_category || null);  // C1b-1a - resume path
      // final_line_items (user-edited) takes precedence when present;
      // requirement_line_items (AI baseline) is also kept separately for
      // "Restore AI-generated lines".
      const aiLineItems = data.requirement_line_items || [];
      setRequirementLineItems(aiLineItems);
      setLineItems((data.final_line_items && data.final_line_items.length > 0) ? data.final_line_items : aiLineItems);
      setRfpDocument(data.rfp_document || null);
      setRfpVersion(data.rfp_version || null);
      setRiskAssessment(data.risk_assessment || null);
      setFeedbackSubmittedVersion(data.feedback_submitted_version ?? null);
      setAttachedDocuments(data.attached_documents || []);
      setBoqItems(data.boq_items || []);
      setBoqSource(data.boq_source || null);
      // G-9: seed the Review step's working vendor-question list from what
      // was actually finalized/revised, not the raw pre-answer AI originals
      // - editableQuestions is what the read-only Review view displays on a
      // Back-navigation revisit, so resuming past Review must not show
      // stale, un-revised questions. final_vendor_questions is already
      // plain strings; revised_vendor_questions is {original, revised}
      // objects (same shape handleAnswersSubmit converts at :639) and needs
      // the same unwrap before use here.
      //
      // Bug batch (PROC-000051): final_vendor_questions is [] at every
      // resume before finalize-questions has ever run (its normal pre-
      // Review-completion state) - `[] || x` short-circuits on the empty
      // array (empty arrays are truthy in JS) and never reaches the
      // populated fallback. Every term must be guarded for non-empty, not
      // just the revised one.
      //
      // F5 (2026-08-29): a removed question is never offered for editing and
      // never reaches final_vendor_questions, so it is filtered out here too.
      // It is not lost - setRemovedVendorQuestions above restores it for the
      // Review surface, which shows it marked as removed with its reason.
      const revisedVendorQuestionsAsStrings = keptEntries(data.revised_vendor_questions || [])
        .map(q => (q && typeof q === 'object') ? (q.revised || q.original || '') : q);
      setEditableQuestions(
        (data.final_vendor_questions?.length > 0 ? data.final_vendor_questions : null)
        || (revisedVendorQuestionsAsStrings.length > 0 ? revisedVendorQuestionsAsStrings : null)
        || (data.vendor_risk_questions?.length > 0 ? data.vendor_risk_questions : null)
        || []
      );
      // Self-repair for procurements saved before rfp_currency was persisted
      // at submit-answers time (Issue 14/BLOCKER): rfp_currency used to be
      // written only at finalize-line-items, so a procurement resumed before
      // Lines was ever finalized loaded with no currency and a permanently
      // disabled Generate RFP button. Derive it the same way the Answers
      // step does, from the already-loaded questions/answers, when missing.
      setSelectedCurrency(
        data.rfp_currency || extractCurrencyAnswer(data.business_questions || [], data.user_answers || {}) || null
      );
      // Answers soft-warning acknowledgment fix (2026-07-25): the
      // deadlineSoonWarningShown/runwayWarningShown per-procurement guards
      // (and their reset here) are gone - soft warnings are now evaluated
      // fresh on every submit attempt (see StepAnswers.jsx's handleNextClick),
      // so resume-time re-fire (Principle 9) happens naturally via the next
      // submit rather than needing an explicit reset on load.

      // Fresh invalidation state for this load - a resumed procurement
      // starts with nothing stale/unlocked; furthestStepReached mirrors how
      // far its backend status says it has actually progressed.
      setStaleStepIds(new Set());
      setStepEditUnlocked(new Set());
      // A resumed procurement has a server-side document; anything held
      // from an earlier drafting session in this tab belongs to a request
      // that is not this one.
      clearHeldFiles();
      setViewedSteps(new Set());
      setRegeneratedNotice(null);
      entrySnapshotsRef.current = {};

      const resumeStep = getStepFromStatus(data.status);

      // NO DATE CHECK ON THE LOAD PATH (Ashok ruling, 2026-08-31).
      // "A generated RFP must be readable. Once rfp_generated, nothing on
      // a read path may block."
      //
      // WHAT USED TO BE HERE, AND WHY IT IS NOT. Item 7 trigger (a),
      // 32561b0 (2026-07-23), ran checkAnswersTemporalValidity /
      // checkLineItemDateRules on every load whose status was not
      // `completed`, and on a hard stop capped BOTH the resume step and
      // furthestStepReached to Math.min(resumeStep, 2) - Answers. An
      // rfp_generated procurement resumes at step 6, so a buyer whose
      // deadline had simply passed was dropped four steps back and could
      // not reach assess_risk at all.
      //
      // 32561b0'S REASONING REMAINS CORRECT AND IS NOT BEING REVERSED.
      // rfp_generated is NOT a freeze boundary: RFP v1 stays editable and
      // regenerable, and regenerating with a past deadline is still
      // refused. What was wrong was the PLACEMENT, not the rule. A past
      // submission deadline BEFORE issue is a defect - vendors cannot
      // respond to it. AFTER generation it is history: the document was
      // issued with a window, and the window closed. Nothing about the
      // document changed; only the calendar moved. PROC-000309 was
      // generated on 2026-08-05 with a 2026-08-20 deadline and began
      // blocking on the 21st with no code change.
      //
      // THE GUARD BLOCKS THE TRANSITION, NEVER THE READ, and it is still
      // in force at every transition:
      //   - routes/procurement.py generate_rfp (both arms) - the server
      //     backstop that no client can skip;
      //   - StepAnswers.jsx, on submit;
      //   - StepLineItems.jsx, the submission-only check before Generate.
      // A buyer who tries to produce a NEW artefact with a past deadline
      // is stopped by those, which is where being stopped is useful.
      //
      // ONE PLACEMENT, NOT THREE FIXES. HVPT, HVND and HVSD are all
      // today-relative hard stops and all three were capping here;
      // removing the call closes all three together. Measured 2026-08-31:
      // 26 of 41 blocked staging documents and 5 of 6 prod ones carried a
      // SECOND past-date code behind the first, so unblocking one code at
      // a time would have surfaced the next on two-thirds of them.
      //
      // NO STATUS AWARENESS IS ADDED, deliberately. check_date_rules takes
      // only dates - no status, no rfp_document, no version - and the one
      // piece of status awareness that used to live here was a single
      // hardcoded string. Placement settles this without teaching the date
      // framework a concept it does not have.
      //
      // temporalBlock ITSELF IS NOT GONE. generateRfp's 400 TEMPORAL_BLOCK
      // handler above still sets it, routes back to Answers and shows the
      // same banner - that is the transition being refused, which is the
      // behaviour this ruling keeps.
      const effectiveResumeStep = resumeStep;
      setTemporalBlock(null);

      setFurthestStepReached(effectiveResumeStep);
      setCurrentStep(options.forceStep ?? effectiveResumeStep);

      return { data, resumeStep: effectiveResumeStep };
    } finally {
      setLoading(false);
    }
    // clearHeldFiles is the only dependency, and it is itself a
    // useCallback with an empty dep array - so this list stays effectively
    // constant and loadProcurement keeps the stable identity
    // ContinueProcurement's load effect depends on. Listing it rather than
    // suppressing the rule keeps that reasoning checkable.
  }, [clearHeldFiles]);

  const value = {
    // State
    currentStep,
    setCurrentStep,
    loading,
    wizardHeaderHeight,
    setWizardHeaderHeight,
    procurementId,
    serialId,  // Expose serial ID
    userRequirement,
    setUserRequirement,
    parsedRequirement,
    setParsedRequirement,
    categoryActiveResolvable,
    masterReferenceAdvisory,
    procurementStatus,
    finalLineCount,
    riskFork,
    riskForkBusy,
    handleRiskForkDecision,
    echoMetadata,
    setEchoMetadata,
    businessQuestions,
    setBusinessQuestions,
    vendorQuestions,
    setVendorQuestions,
    answers,
    setAnswers,
    revisedVendorQuestions,
    removedVendorQuestions,
    revisionSummary,
    editableQuestions,
    setEditableQuestions,
    refinedRequirement,
    setRefinedRequirement,
    lineItems,
    setLineItems,
    rfpDocument,
    setRfpDocument,
    rfpVersion,
    setRfpVersion,
    riskAssessment,
    setRiskAssessment,
    feedbackSubmittedVersion,
    setFeedbackSubmittedVersion,
    boqItems,
    setBoqItems,
    boqSource,
    setBoqSource,
    attachedDocuments,
    setAttachedDocuments,
    consistencyValidation,
    selectedCurrency,
    setSelectedCurrency,
    temporalBlock,
    setTemporalBlock,
    requirementLineItems,
    setRequirementLineItems,
    lineItemErrors,

    // Unsaved-changes navigation guard (pre-merge item)
    dirtySteps,
    markStepDirty,
    clearStepDirty,
    hasUnsavedChanges,

    // Wizard invalidation (P2 §4)
    stepStates,
    staleStepIds,
    furthestStepReached,
    regeneratedNotice,
    setRegeneratedNotice,
    isStepPreviouslyCompleted,
    isStepUnlocked,
    unlockStepForEdit,
    lockStepEdit,
    markStepsStale,
    clearStepStale,
    postWizardEvent,

    // Derived
    getProcurementType,
    getAssessedSpendCategory,
    getShipToLocation,
    getSubmissionDeadlineAnswer,

    // Handlers
    // Held-until-Continue client state (Intake Compression V1).
    getTempRequestId,
    heldBoqFile,
    setHeldBoqFile,
    heldDocuments,
    setHeldDocuments,
    handleIntakeSubmit,
    handleIntakeEditSubmit,
    handleCategoryOverride,
    handleCategoryDeclareNoFit,
    handleUploadsComplete,
    handleProceedToAnswers,
    handleAnswersSubmit,
    confirmAdvanceToLineItems,
    handleFinalizeQuestions,
    handleRestoreAiLines,
    handleFinalizeLineItems,
    handleProceedToRisk,
    handleComplete,
    handleQuestionEdit,
    handleAddQuestion,
    handleRemoveQuestion,
    handleAddBusinessQuestion,
    handleEditBusinessQuestion,
    handleRemoveBusinessQuestion,
    loadProcurement,
  };

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
};

export default WizardContext;
